import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  ChevronLeft, 
  Building, 
  Palette, 
  Globe, 
  Phone, 
  Mail,
  FileText,
  Download,
  RefreshCw,
  Image,
  ExternalLink,
  Zap,
  CheckCircle2,
  MessageSquare,
  Eye,
  Link2,
  AlertTriangle,
  Check,
  Loader2,
  Edit3,
  XCircle,
  Sparkles
} from 'lucide-react';
import { CalendarDaysIcon, ClockIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Modal, Button, IconButton } from '@/components/ui';
import { BusinessBrandingService, CompanySettingsService, AttachmentsService, TenantRegistrationService, BookingFormService } from '../../api';
import { isValidHexColor, normalizeHexColor } from '../../utils/colorUtils';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { sanitizeFileForUpload } from '../../utils/fileUtils';
import { isUsableLogoUrl } from '../../utils/brandSettings';
import {
  fetchBrandAssets,
  raceToFirstSuccess,
  type BrandFetchResult,
} from '../../business/branding/brandFetch';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useTenantData } from '../../contexts/TenantDataContext';
import { Routemize_Constants_BookingFormStatus } from '../../api/models/Routemize_Constants_BookingFormStatus';
import type { Routemize_DTOs_BusinessBrandingDTO, Routemize_DTOs_CompanySettingsDTO } from '../../api';
import CompanyLogo from '../shared/CompanyLogo';

// ============================
// IMAGE COLOR EXTRACTION
// ============================

// Detect MIME type from URL extension or ArrayBuffer magic bytes
const detectImageMime = (url: string, buffer?: ArrayBuffer): string => {
  if (buffer && buffer.byteLength >= 4) {
    const bytes = new Uint8Array(buffer.slice(0, 4));
    if (bytes[0] === 0x89 && bytes[1] === 0x50) return 'image/png';
    if (bytes[0] === 0xFF && bytes[1] === 0xD8) return 'image/jpeg';
    if (bytes[0] === 0x47 && bytes[1] === 0x49) return 'image/gif';
    if (bytes[0] === 0x52 && bytes[1] === 0x49) return 'image/webp';
  }
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
  const mimeMap: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', ico: 'image/x-icon' };
  return mimeMap[ext || ''] || 'image/png';
};

// Load a same-origin image URL into a canvas and extract a brand color palette.
//
// Tracks chromatic and neutral (black/white/gray) colors separately so neutral
// brand colors (e.g. logos that are primarily black, white, or charcoal) are
// surfaced alongside vibrant accents instead of being filtered out.
//
// Ranking is dominated by raw pixel frequency — saturation is only a small
// tiebreaker, so a small-but-vibrant accent dot can no longer outrank the
// dominant brand color. A minimum-share threshold also drops tiny accent
// regions entirely. When `boostHexes` is supplied (e.g. colors found in the
// site's CSS), matching colors are promoted and bypass the share threshold,
// since cross-confirmation between the logo and CSS is a strong brand signal.
const extractPixelColors = (
  imgSrc: string,
  options?: { boostHexes?: string[] }
): Promise<string[]> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    const timeout = setTimeout(() => { resolve([]); }, 8000);

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve([]); return; }

        // Sample at a higher resolution than before so small but important
        // accent areas (e.g. a single colored dot in a logo) survive sampling.
        const scale = Math.min(1, 256 / Math.max(img.width, img.height));
        canvas.width = Math.max(1, Math.floor(img.width * scale));
        canvas.height = Math.max(1, Math.floor(img.height * scale));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        const QUANT = 8; // finer quantization preserves distinct brand shades
        const NEUTRAL_SAT_THRESHOLD = 22; // max-min below this counts as neutral

        // Pre-quantize each boost color (and a ±1-quantum neighborhood) so we
        // can match logo buckets cheaply via set lookup despite small color
        // differences between rendered pixels and authored CSS values.
        const boostKeys = new Set<string>();
        if (options?.boostHexes) {
          for (const hex of options.boostHexes) {
            const cleaned = hex.replace('#', '').trim();
            if (cleaned.length !== 6) continue;
            const r = parseInt(cleaned.slice(0, 2), 16);
            const g = parseInt(cleaned.slice(2, 4), 16);
            const b = parseInt(cleaned.slice(4, 6), 16);
            if ([r, g, b].some(v => Number.isNaN(v))) continue;
            for (let dr = -1; dr <= 1; dr++) {
              for (let dg = -1; dg <= 1; dg++) {
                for (let db = -1; db <= 1; db++) {
                  const br = (Math.round(r / QUANT) + dr) * QUANT;
                  const bg = (Math.round(g / QUANT) + dg) * QUANT;
                  const bb = (Math.round(b / QUANT) + db) * QUANT;
                  if (br < 0 || bg < 0 || bb < 0) continue;
                  if (br > 255 || bg > 255 || bb > 255) continue;
                  boostKeys.add(`${br},${bg},${bb}`);
                }
              }
            }
          }
        }

        type Bucket = { r: number; g: number; b: number; count: number; sat: number; key: string; boosted: boolean };
        const chromaBuckets: Record<string, Bucket> = {};
        const neutralBuckets: Record<string, Bucket> = {};
        let totalKept = 0;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];
          if (a < 128) continue; // skip transparent / mostly-transparent pixels

          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const sat = max - min;

          const br = Math.round(r / QUANT) * QUANT;
          const bg = Math.round(g / QUANT) * QUANT;
          const bb = Math.round(b / QUANT) * QUANT;
          const key = `${br},${bg},${bb}`;
          const boosted = boostKeys.has(key);

          if (sat < NEUTRAL_SAT_THRESHOLD) {
            // Neutral (black/white/gray). Skip only the absolute extremes that
            // are almost certainly background or anti-alias noise — keep
            // everything else so brand neutrals can surface.
            if (max > 250) continue; // pure white background
            if (max < 6) continue;   // pure black noise
            const bucket = neutralBuckets[key] || (neutralBuckets[key] = { r: br, g: bg, b: bb, count: 0, sat, key, boosted });
            bucket.count++;
          } else {
            const bucket = chromaBuckets[key] || (chromaBuckets[key] = { r: br, g: bg, b: bb, count: 0, sat, key, boosted });
            bucket.count++;
            if (sat > bucket.sat) bucket.sat = sat;
          }
          totalKept++;
        }

        // Frequency dominates the score. A tiny saturation tiebreaker keeps
        // vibrant pixels just above near-equal pastels of the same count, but
        // can't promote an accent dot over a true dominant brand color the way
        // the previous 1.5x boost could. Colors that also appear in the site's
        // CSS get a 2.5x boost — strong evidence they are intentional brand.
        const SAT_TIEBREAKER = 0.3;
        const CSS_BOOST = 2.5;
        const chromaSorted = Object.values(chromaBuckets)
          .map(b => ({
            ...b,
            score: b.count * (1 + (b.sat / 255) * SAT_TIEBREAKER) * (b.boosted ? CSS_BOOST : 1),
          }))
          .sort((a, b) => b.score - a.score);
        const neutralSorted = Object.values(neutralBuckets).sort((a, b) => b.count - a.count);

        const MAX_COLORS = 8;
        const DEDUP_DISTANCE = 40; // a touch wider so near-duplicate shades collapse
        const MIN_NEUTRAL_SHARE = 0.015; // neutrals need ~1.5% presence to appear
        const MIN_CHROMA_SHARE = 0.02;   // chromatic accents need ~2% to surface

        const result: string[] = [];
        const tryAdd = (b: Bucket) => {
          const tooClose = result.some(ex => {
            const er = parseInt(ex.slice(1, 3), 16);
            const eg = parseInt(ex.slice(3, 5), 16);
            const eb = parseInt(ex.slice(5, 7), 16);
            return Math.sqrt((b.r - er) ** 2 + (b.g - eg) ** 2 + (b.b - eb) ** 2) < DEDUP_DISTANCE;
          });
          if (tooClose) return;
          const hex = '#' + [b.r, b.g, b.b]
            .map(c => Math.min(255, Math.max(0, c)).toString(16).padStart(2, '0'))
            .join('');
          result.push(hex);
        };

        for (const b of chromaSorted) {
          if (result.length >= MAX_COLORS) break;
          // CSS-confirmed colors bypass the share floor — if the brand uses
          // a color in their stylesheet we trust it even when the logo only
          // contains a small swatch of it.
          if (!b.boosted && totalKept > 0 && b.count / totalKept < MIN_CHROMA_SHARE) continue;
          tryAdd(b);
        }

        for (const b of neutralSorted) {
          if (result.length >= MAX_COLORS) break;
          if (totalKept > 0 && b.count / totalKept < MIN_NEUTRAL_SHARE) continue;
          tryAdd(b);
        }

        resolve(result);
      } catch (err) {
        resolve([]);
      }
    };

    img.onerror = () => { clearTimeout(timeout); resolve([]); };
    img.src = imgSrc;
  });
};

// Main entry: extract dominant colors from any image URL.
// For local blob:/data: URLs, reads directly. For remote URLs, fetches bytes
// through a CORS proxy and creates a local blob URL so canvas is never tainted.
//
// `boostHexes` (optional) lets callers pass colors gathered from a stronger
// signal (e.g. the site's CSS) so the pixel ranker can prefer logo regions
// that match those colors and downplay incidental accents.
const extractColorsFromImage = async (
  imageUrl: string,
  options?: { boostHexes?: string[] }
): Promise<string[]> => {
  // Local URLs (blob:, data:) are same-origin — canvas works directly
  if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
    const colors = await extractPixelColors(imageUrl, options);
    return colors;
  }
  
  // Remote URLs: fetch image bytes through CORS proxies (all fired in parallel,
  // first usable response wins) and create a local blob URL so the canvas is
  // never tainted. Racing the proxies avoids the previous sequential waterfall
  // where a slow/dead proxy stalled extraction for up to 30s.
  //
  // wsrv.nl (images.weserv.nl) leads the list: it's a dedicated image proxy
  // that responds quickly, sends `Access-Control-Allow-Origin: *`, and
  // re-encodes the response to a real raster PNG — which also rasterizes SVG
  // logos that a tainted/zero-dimension canvas otherwise can't sample. It
  // replaces corsproxy.io, which now serves an HTML landing page instead of
  // image bytes, breaking color extraction for every scraped logo.
  const proxies = [
    `https://wsrv.nl/?url=${encodeURIComponent(imageUrl)}&output=png&w=512`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(imageUrl)}`,
    `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(imageUrl)}`,
  ];

  try {
    return await raceToFirstSuccess(
      proxies.map((proxyUrl) => async () => {
        const response = await fetch(proxyUrl, { signal: AbortSignal.timeout(7000) });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const buffer = await response.arrayBuffer();
        if (buffer.byteLength < 50) throw new Error('Empty image');

        // Detect type from magic bytes, not from the proxy's content-type header
        const mime = detectImageMime(imageUrl, buffer);
        const blob = new Blob([buffer], { type: mime });
        const blobUrl = URL.createObjectURL(blob);

        const colors = await extractPixelColors(blobUrl, options);
        URL.revokeObjectURL(blobUrl);

        if (colors.length === 0) throw new Error('No colors extracted');
        return colors;
      })
    );
  } catch {
    return [];
  }
};

// Pick the highest-frequency CSS-derived hex colors to use as a boost signal
// for logo color extraction. We cap at a small number so a stylesheet that
// happens to use 30 different shades doesn't dilute the signal.
const topCssBoostHexes = (
  frequencies?: Record<string, number>,
  limit = 8
): string[] => {
  if (!frequencies) return [];
  return Object.entries(frequencies)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([hex]) => hex);
};

// After logo extraction, surface any colors that the site's CSS also uses to
// the top of the list. The pixel ranker already gives them a score boost, but
// re-sorting here guarantees they appear first in the user's suggestion chips
// even when an unrelated logo color happened to outrank them on raw share.
const sortCssConfirmedFirst = (
  logoColors: string[],
  boostHexes: string[]
): string[] => {
  if (boostHexes.length === 0) return logoColors;
  const boostKeys = new Set(boostHexes.map(h => h.toLowerCase()));
  const NEAR = 24; // RGB euclidean distance for a "close enough" match
  const parse = (hex: string): [number, number, number] => {
    const c = hex.replace('#', '');
    return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
  };
  const isCssConfirmed = (hex: string): boolean => {
    const lower = hex.toLowerCase();
    if (boostKeys.has(lower)) return true;
    const [r, g, b] = parse(hex);
    for (const boost of boostHexes) {
      const [br, bg, bb] = parse(boost);
      if (Math.sqrt((r - br) ** 2 + (g - bg) ** 2 + (b - bb) ** 2) < NEAR) return true;
    }
    return false;
  };
  const confirmed = logoColors.filter(isCssConfirmed);
  const remaining = logoColors.filter(c => !isCssConfirmed(c));
  return [...confirmed, ...remaining];
};

// ============================
// BRANDING PREVIEW COMPONENTS
// ============================

// Helper to determine if color is light or dark for text contrast
const isLightColor = (color: string) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

// Mini booking form preview showing live brand colors
const BookingFormPreview = React.memo(function BookingFormPreview({ 
  businessName = 'Your Business',
  primaryColor = '#3B82F6',
  logoUrl
}: { 
  businessName?: string; 
  primaryColor?: string;
  logoUrl?: string;
}) {
  const [selectedDate, setSelectedDate] = useState(1);
  
  // Auto-cycle through date selections for animation
  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedDate((prev) => (prev % 3) + 1);
    }, 2500);
    return () => clearInterval(interval);
  }, []);
  
  const dates = [
    { day: 'TUE', date: '14', month: 'Jan' },
    { day: 'WED', date: '15', month: 'Jan' },
    { day: 'THU', date: '16', month: 'Jan' },
  ];

  const textColor = isLightColor(primaryColor) ? '#1a1a1a' : '#ffffff';
  
  return (
    <div className="relative bg-ink-100 rounded-xl p-2 border border-ink-200 shadow-inner">
      {/* Phone frame mockup */}
      <div className="bg-surface rounded-lg shadow-md border border-ink-100 overflow-hidden">
        {/* Content */}
        <div className="p-3">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={businessName} 
                className="h-8 max-w-[80px] object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300"
                style={{ backgroundColor: primaryColor }}
              >
                <span style={{ color: textColor }} className="text-xs font-bold">
                  {businessName?.charAt(0)?.toUpperCase() || 'B'}
                </span>
              </div>
            )}
          </div>
          
          {/* Title with brand color */}
          <h3 className="text-center text-xs font-bold text-ink-900 mb-0.5">
            Schedule Your{' '}
            <span className="transition-colors duration-300" style={{ color: primaryColor }}>Appointment</span>
          </h3>
          
          {/* Subtitle */}
          <p className="text-center text-2xs text-ink-500 mb-2">What's your preferred day?</p>
          
          {/* Date cards */}
          <div className="flex gap-1.5 mb-2">
            {dates.map((d, i) => (
              <div 
                key={i}
                className={`flex-1 rounded-md border p-1.5 text-center transition-all duration-300 ${
                  selectedDate === i + 1 
                    ? 'shadow-sm scale-105' 
                    : 'border-ink-200 bg-surface'
                }`}
                style={selectedDate === i + 1 ? { 
                  borderColor: primaryColor,
                  backgroundColor: `${primaryColor}10`
                } : {}}
              >
                {/* Instant badge */}
                <div 
                  className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-medium mb-0.5 transition-colors duration-300"
                  style={selectedDate === i + 1 ? { 
                    backgroundColor: `${primaryColor}20`,
                    color: primaryColor
                  } : {
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                  }}
                >
                  <Zap className="w-2 h-2" />
                  <span>INSTANT</span>
                </div>
                <p className="text-2xs text-ink-400">{d.day}</p>
                <p className="text-sm font-bold text-ink-900">{d.date}</p>
                <p className="text-2xs text-ink-400">{d.month}</p>
              </div>
            ))}
          </div>
          
          {/* Different date button */}
          <div className="border border-dashed border-ink-200 rounded-md p-1.5 text-center mb-2">
            <div className="flex items-center justify-center gap-1 text-9 text-ink-400">
              <CalendarDaysIcon className="w-2.5 h-2.5" />
              <span>Choose a different date</span>
            </div>
          </div>
          
          {/* Footer badges */}
          <div className="flex items-center justify-center gap-2 pt-1.5 border-t border-ink-100">
            <div className="flex items-center gap-0.5 text-9 text-ink-400">
              <ClockIcon className="w-2.5 h-2.5" />
              <span>2 min</span>
            </div>
            <div className="w-px h-2 bg-ink-200" />
            <div className="flex items-center gap-0.5 text-9 text-ink-400">
              <CheckCircle2 className="w-2.5 h-2.5" />
              <span>Secure</span>
            </div>
          </div>
        </div>
        
        {/* Powered by footer */}
        <div className="bg-ink-50 py-1.5 text-center border-t border-ink-100">
          <p className="text-9 text-ink-400">
            Powered by <span className="font-semibold text-ink-500">routemize</span>
          </p>
        </div>
      </div>
      
      {/* Label */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-600 text-white text-2xs font-medium rounded-full shadow-sm whitespace-nowrap">
        Booking Form
      </div>
    </div>
  );
});

// Chatbot preview showing live brand colors - mimics actual button-based flow
const ChatbotPreview = React.memo(function ChatbotPreview({ 
  businessName = 'Your Business',
  primaryColor = '#3B82F6',
  logoUrl
}: { 
  businessName?: string; 
  primaryColor?: string;
  logoUrl?: string;
}) {
  const [chatStep, setChatStep] = useState(0);
  const [selectedButton, setSelectedButton] = useState<number | null>(null);
  
  // Auto-cycle through chat flow: greeting → show buttons → user selects → next message (slower)
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    
    const cycleMessages = () => {
      setChatStep(0);
      setSelectedButton(null);
      
      // Step 1: Show greeting
      setTimeout(() => {
        setChatStep(1);
        
        // Step 2: After delay, "click" a button (slower)
        setTimeout(() => {
          setSelectedButton(0); // Select first button
          
          // Step 3: Show next bot message (slower)
          setTimeout(() => {
            setChatStep(2);
            
            // Reset and loop (longer pause)
            timeout = setTimeout(cycleMessages, 4000);
          }, 1000);
        }, 2500);
      }, 800);
    };
    
    cycleMessages();
    return () => clearTimeout(timeout);
  }, []);

  const textColor = isLightColor(primaryColor) ? '#1a1a1a' : '#ffffff';
  
  return (
    <div className="flex justify-center">
      <div className="relative bg-ink-100 rounded-xl p-3 border border-ink-200 shadow-inner w-[280px]">
        {/* Chat window mockup */}
        <div className="bg-surface rounded-lg shadow-md border border-ink-100 overflow-hidden">
          {/* Chat header */}
          <div 
            className="px-3 py-2.5 flex items-center gap-2.5 transition-colors duration-300"
            style={{ backgroundColor: primaryColor }}
          >
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={businessName} 
                className="h-7 w-7 rounded-full object-cover bg-surface"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <span style={{ color: textColor }} className="text-xs font-bold">
                  {businessName?.charAt(0)?.toUpperCase() || 'B'}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p style={{ color: textColor }} className="text-sm font-semibold leading-tight truncate">
                {businessName}
              </p>
              <p style={{ color: textColor, opacity: 0.7 }} className="text-2xs">
                Booking Assistant
              </p>
            </div>
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          </div>
          
          {/* Chat messages */}
          <div className="p-3 space-y-3 min-h-[180px]">
            {/* Bot greeting with quick reply buttons */}
            <div className={`transition-all duration-300 ${chatStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
              <div className="flex items-start gap-2 mb-2.5">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <MessageSquare className="w-3 h-3" style={{ color: primaryColor }} />
                </div>
                <div className="bg-ink-50 rounded-lg rounded-tl-sm px-3 py-2">
                  <p className="text-xs text-ink-700">
                    👋 When would you like to schedule?
                  </p>
                </div>
              </div>
              
              {/* Quick reply buttons - like actual chatbot */}
              {chatStep >= 1 && selectedButton === null && (
                <div className="pl-8 flex flex-col gap-1.5">
                  {['Mon, Jan 20', 'Tue, Jan 21', 'Other date'].map((date, i) => (
                    <div 
                      key={i}
                      className="px-3 py-1.5 rounded-full border text-11 font-medium transition-all duration-200 text-center"
                      style={{ 
                        borderColor: `${primaryColor}40`,
                        color: primaryColor,
                        backgroundColor: `${primaryColor}08`
                      }}
                    >
                      {date}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* User selected button response */}
            {selectedButton !== null && (
              <div className="flex justify-end transition-all duration-300">
                <div 
                  className="rounded-full px-3 py-1.5 transition-colors duration-300"
                  style={{ backgroundColor: primaryColor }}
                >
                  <p className="text-xs font-medium" style={{ color: textColor }}>
                    Mon, Jan 20
                  </p>
                </div>
              </div>
            )}
            
            {/* Bot follow-up with time buttons */}
            {chatStep >= 2 && (
              <div className="transition-all duration-300">
                <div className="flex items-start gap-2 mb-2.5">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <MessageSquare className="w-3 h-3" style={{ color: primaryColor }} />
                  </div>
                  <div className="bg-ink-50 rounded-lg rounded-tl-sm px-3 py-2">
                    <p className="text-xs text-ink-700">
                      Great! What time works best?
                    </p>
                  </div>
                </div>
                
                {/* Time buttons */}
                <div className="pl-8 flex flex-col gap-1.5">
                  {['Morning', 'Afternoon'].map((time, i) => (
                    <div 
                      key={i}
                      className="px-3 py-1.5 rounded-full border text-11 font-medium text-center"
                      style={{ 
                        borderColor: `${primaryColor}40`,
                        color: primaryColor,
                        backgroundColor: `${primaryColor}08`
                      }}
                    >
                      {time}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Powered by footer */}
          <div className="bg-ink-50 py-2 text-center border-t border-ink-100">
            <p className="text-2xs text-ink-400">
              Powered by <span className="font-semibold text-ink-500">routemize</span>
            </p>
          </div>
        </div>
        
        {/* Label */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm whitespace-nowrap">
          Chatbot
        </div>
      </div>
    </div>
  );
});

// Tabbed preview showing both booking form and chatbot with live branding
const TabbedBrandingPreview = React.memo(function TabbedBrandingPreview({ businessName, primaryColor, logoUrl }: { 
  businessName?: string; 
  primaryColor?: string;
  logoUrl?: string;
}) {
  const [activeTab, setActiveTab] = useState<'booking' | 'chatbot'>('booking');
  const [autoRotate, setAutoRotate] = useState(true);
  
  // Auto-rotate between tabs
  useEffect(() => {
    if (!autoRotate) return;
    
    const interval = setInterval(() => {
      setActiveTab(prev => prev === 'booking' ? 'chatbot' : 'booking');
    }, 6000);
    
    return () => clearInterval(interval);
  }, [autoRotate]);
  
  const handleTabClick = (tab: 'booking' | 'chatbot') => {
    setActiveTab(tab);
    setAutoRotate(false); // Stop auto-rotate when user clicks
  };
  
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-500 flex items-center justify-center">
          <Eye className="w-4 h-4 text-white" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-ink-900">Live Branding Preview</h4>
          <p className="text-xs text-ink-500">See how your brand appears to customers</p>
        </div>
      </div>
      
      {/* Tab buttons */}
      {/* eslint-disable no-restricted-syntax -- Preview-pane segmented tabs (Booking vs Chatbot) — text-blue-600 active color is intentional brand contrast against neutral inactive; doesn't fit segmented-pill TabButton */}
      <div className="flex gap-1 p-1 bg-ink-100 rounded-lg">
        <button
          onClick={() => handleTabClick('booking')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
            activeTab === 'booking'
              ? 'bg-surface shadow-sm text-blue-600'
              : 'text-ink-500 hover:text-ink-700'
          }`}
        >
          <CalendarDaysIcon className="w-3.5 h-3.5" />
          <span>Booking Form</span>
        </button>
        <button
          onClick={() => handleTabClick('chatbot')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
            activeTab === 'chatbot'
              ? 'bg-surface shadow-sm text-blue-600'
              : 'text-ink-500 hover:text-ink-700'
          }`}
        >
          <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
          <span>Chatbot</span>
        </button>
      </div>
      {/* eslint-enable no-restricted-syntax */}

      {/* Preview content */}
      <div className="relative min-h-[340px]">
        <div className={`transition-all duration-300 ${activeTab === 'booking' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'}`}>
          <BookingFormPreview 
            businessName={businessName}
            primaryColor={primaryColor}
            logoUrl={logoUrl}
          />
        </div>
        <div className={`transition-all duration-300 ${activeTab === 'chatbot' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'}`}>
          <ChatbotPreview 
            businessName={businessName}
            primaryColor={primaryColor}
            logoUrl={logoUrl}
          />
        </div>
      </div>
      
      {/* Auto-rotate indicator */}
      <div className="flex items-center justify-center gap-2">
        <div className="flex gap-1">
          <div className={`w-1.5 h-1.5 rounded-full transition-colors ${activeTab === 'booking' ? 'bg-blue-500' : 'bg-ink-300'}`} />
          <div className={`w-1.5 h-1.5 rounded-full transition-colors ${activeTab === 'chatbot' ? 'bg-blue-500' : 'bg-ink-300'}`} />
        </div>
        {autoRotate && <span className="text-2xs text-ink-400">Auto-previewing</span>}
      </div>
      
      {/* Info callout */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-100 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <CheckCircle2 className="w-3 h-3 text-blue-600" />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-ink-700">Your branding will appear on:</p>
            <ul className="text-2xs text-ink-600 space-y-0.5">
              <li>• Public booking forms</li>
              <li>• Embedded chatbot widgets</li>
              <li>• Customer confirmation emails</li>
              <li>• Appointment reminders</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================
// END BRANDING PREVIEW COMPONENTS
// ============================

// ============================
// LOGO SELECTION GRID
// ============================

// Individual logo card with its own error state — prevents stale DOM from innerHTML hacks
const LogoCard: React.FC<{
  logo: { url: string; type: 'logo' | 'icon' | 'og-image' | 'favicon'; label: string };
  isSelected: boolean;
  animate: boolean;
  onSelect: (url: string) => void;
}> = ({ logo, isSelected, animate, onSelect }) => {
  const [imgError, setImgError] = useState(false);
  
  const typeIcon = logo.type === 'icon' || logo.type === 'favicon' ? '🔹' : logo.type === 'og-image' ? '🖼️' : '📌';
  const typeLabel = logo.type === 'icon' ? 'Good for chatbot' : logo.type === 'favicon' ? 'Favicon (small)' : logo.type === 'og-image' ? 'Social preview' : 'Full logo';
  
  return (
    // eslint-disable-next-line no-restricted-syntax -- Logo selection card (flex-col with image + label + ring on selected); large clickable card, not button shape
    <button
      onClick={() => onSelect(logo.url)}
      className={`relative flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:shadow-md ${
        isSelected
          ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
          : `border-ink-200 hover:border-blue-400 hover:bg-blue-50 ${animate ? 'apply-btn-pulse' : ''}`
      }`}
      title={`${logo.label}\n${typeLabel}`}
    >
      {isSelected && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
      
      <div className="w-16 h-16 flex items-center justify-center bg-ink-50 rounded border border-ink-200 mb-2 overflow-hidden">
        {imgError ? (
          <div className="text-xs text-ink-400 text-center">Preview<br/>unavailable</div>
        ) : (
          <img 
            src={logo.url} 
            alt={logo.label}
            className="max-h-full max-w-full object-contain"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      
      <span className="text-2xs text-ink-500 text-center leading-tight">
        {typeIcon} {logo.label}
      </span>
      <span className={`text-9 mt-0.5 ${
        logo.type === 'icon' || logo.type === 'favicon' ? 'text-blue-600 font-medium' : 'text-ink-400'
      }`}>
        {typeLabel}
      </span>
    </button>
  );
};

// Grid of logo cards. Uses the logo URL as React key so cards are fully
// recreated when scrape data changes (no stale error states).
const LogoGrid: React.FC<{
  logos: Array<{ url: string; type: 'logo' | 'icon' | 'og-image' | 'favicon'; label: string }>;
  selectedUrl?: string;
  animateApplyButtons: boolean;
  onSelect: (url: string) => void;
}> = ({ logos, selectedUrl, animateApplyButtons, onSelect }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {logos.map((logo) => (
      <LogoCard
        key={logo.url}
        logo={logo}
        isSelected={selectedUrl === logo.url}
        animate={animateApplyButtons}
        onSelect={onSelect}
      />
    ))}
  </div>
);

// ============================
// END LOGO SELECTION GRID
// ============================

interface BusinessBrandingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean;
  onSave?: () => Promise<void>;
  onDataChange?: (data: Routemize_DTOs_BusinessBrandingDTO) => void;
}

// Discovery lives in business/branding/brandFetch.ts. `cssColorFrequencies`
// doubles as a boost signal for logo pixel extraction — a color the logo and
// the stylesheet agree on is almost certainly a brand color — and `cssColors`
// is the fallback palette when pixel extraction comes back empty.
type WebsiteData = BrandFetchResult;

const EnhancedBusinessBrandingPanel: React.FC<BusinessBrandingPanelProps> = ({ isOpen, onClose, inline = false, onSave, onDataChange }) => {
  const [settings, setSettings] = useState<Routemize_DTOs_BusinessBrandingDTO>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scrapingWebsite, setScrapingWebsite] = useState(false);
  const [websiteData, setWebsiteData] = useState<WebsiteData | null>(null);
  const [showWebsiteScraper, setShowWebsiteScraper] = useState(false);
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const [appliedField, setAppliedField] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedColorOrder, setSelectedColorOrder] = useState<string[]>([]);
  const [uploadingManualLogo, setUploadingManualLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  // Chatbot avatar icon (tenant-level Business Branding). Falls back to the
  // company logo when empty. Shares the logo's S3-upload flow.
  const [uploadingChatbotIcon, setUploadingChatbotIcon] = useState(false);
  const [chatbotIconUploadError, setChatbotIconUploadError] = useState<string | null>(null);
  // Local object URL of the just-dropped/selected file, shown immediately so
  // the user sees their logo before the S3 upload round-trip completes.
  const [pendingLogoPreview, setPendingLogoPreview] = useState<string | null>(null);
  const [logoJustUploaded, setLogoJustUploaded] = useState(false);
  const logoUploadedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors pendingLogoPreview so the unmount cleanup can revoke the blob URL.
  const pendingLogoPreviewRef = useRef<string | null>(null);

  const replacePendingLogoPreview = useCallback((next: string | null) => {
    if (pendingLogoPreviewRef.current) URL.revokeObjectURL(pendingLogoPreviewRef.current);
    pendingLogoPreviewRef.current = next;
    setPendingLogoPreview(next);
  }, []);

  const clearPendingLogoPreview = useCallback(() => {
    replacePendingLogoPreview(null);
  }, [replacePendingLogoPreview]);

  useEffect(() => {
    return () => {
      if (logoUploadedTimeoutRef.current) clearTimeout(logoUploadedTimeoutRef.current);
      if (pendingLogoPreviewRef.current) URL.revokeObjectURL(pendingLogoPreviewRef.current);
    };
  }, []);
  // Tracks nested dragenter/dragleave pairs so the highlight doesn't flicker
  // when the cursor moves over child elements inside the drop zone.
  const logoDragDepth = useRef(0);
  const [animateApplyButtons, setAnimateApplyButtons] = useState(false);
  const [extractingLogoColors, setExtractingLogoColors] = useState(false);
  const [logoColorsExtracted, setLogoColorsExtracted] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [colorErrors, setColorErrors] = useState<Record<string, string | null>>({});

  // Toast notification state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'info' | 'colors' | 'error';
    colorPreview?: string[];
    visible: boolean;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const showToast = useCallback((message: string, type: 'success' | 'info' | 'colors' | 'error' = 'success', colorPreview?: string[]) => {
    // Clear previous timeout
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    
    // Show toast
    setToast({ message, type, colorPreview, visible: true });
    
    // Auto-hide after 3.5s
    toastTimeoutRef.current = setTimeout(() => {
      setToast(prev => prev ? { ...prev, visible: false } : null);
      // Remove from DOM after fade-out animation
      setTimeout(() => setToast(null), 300);
    }, 3500);
  }, []);

  // Inject pulse animation styles into the document
  useEffect(() => {
    if (document.getElementById('apply-btn-pulse-style')) return;
    const style = document.createElement('style');
    style.id = 'apply-btn-pulse-style';
    style.textContent = `
      @keyframes applyBtnPulse {
        0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.55); transform: scale(1); }
        50% { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); transform: scale(1.04); }
        100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); transform: scale(1); }
      }
      @keyframes applyAllBtnPulse {
        0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.55); transform: scale(1); }
        50% { box-shadow: 0 0 0 10px rgba(22, 163, 74, 0); transform: scale(1.06); }
        100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); transform: scale(1); }
      }
      .apply-btn-pulse { animation: applyBtnPulse 1.5s ease-in-out infinite; }
      .apply-all-btn-pulse { animation: applyAllBtnPulse 1.2s ease-in-out infinite; }
      @keyframes toastSlideIn {
        0% { transform: translateX(120%); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
      }
      @keyframes toastSlideOut {
        0% { transform: translateX(0); opacity: 1; }
        100% { transform: translateX(120%); opacity: 0; }
      }
      .toast-enter { animation: toastSlideIn 0.35s ease-out forwards; }
      .toast-exit { animation: toastSlideOut 0.3s ease-in forwards; }
      @keyframes toastProgress {
        0% { transform: scaleX(1); }
        100% { transform: scaleX(0); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const existing = document.getElementById('apply-btn-pulse-style');
      if (existing) existing.remove();
    };
  }, []);

  // Trigger apply button animation when website data is found
  useEffect(() => {
    if (websiteData && !websiteData.isFallback) {
      setAnimateApplyButtons(true);
      // Stop the animation after 8 seconds so it doesn't run forever
      const timeout = setTimeout(() => setAnimateApplyButtons(false), 8000);
      return () => clearTimeout(timeout);
    } else {
      setAnimateApplyButtons(false);
    }
  }, [websiteData]);

  // Subdomain settings state
  const [companySettings, setCompanySettings] = useState<Routemize_DTOs_CompanySettingsDTO | null>(null);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);
  const [subdomainInput, setSubdomainInput] = useState('');
  const [originalSubdomain, setOriginalSubdomain] = useState('');
  const [subdomainAvailability, setSubdomainAvailability] = useState<{
    isAvailable: boolean | null;
    message: string | null;
    checking: boolean;
    normalizedSubdomain: string | null;
  }>({ isAvailable: null, message: null, checking: false, normalizedSubdomain: null });
  const [subdomainChangeAcknowledged, setSubdomainChangeAcknowledged] = useState(false);
  const [savingSubdomain, setSavingSubdomain] = useState(false);
  const [subdomainSaveError, setSubdomainSaveError] = useState<string | null>(null);
  const subdomainCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeBookingFormsCount, setActiveBookingFormsCount] = useState(0);

  // Timezone state
  const [savingTimezone, setSavingTimezone] = useState(false);

  // Country state. Changing the tenant country drives ZIP/postal + State/Province
  // labels, validation, units, and service-area keying across the app, so persist
  // it then refresh the shared company-data context.
  const [savingCountry, setSavingCountry] = useState(false);
  const { refreshCompanySettings } = useTenantData();

  // Business phone state — locally controlled draft so we can save on blur
  // (CompanySettings.phone drives the area code used to provision Voice Agent numbers).
  // Format on load via the shared phoneUtils helper so the input always renders
  // in the canonical (NPA) NXX-XXXX shape regardless of how it was stored.
  const [phoneDraft, setPhoneDraft] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const voiceAgentAccess = useFeatureAccess('VoiceAgent');
  useEffect(() => {
    setPhoneDraft(formatPhoneNumber(companySettings?.phone ?? ''));
  }, [companySettings?.phone]);

  // Load settings when panel opens
  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [companyData, bookingFormsData] = await Promise.all([
        CompanySettingsService.getApiAppCompanySettingsGet(),
        // Explicit MaxResultCount — ABP's default page size is 10, which would
        // silently drop forms for tenants with more than 10 booking forms.
        BookingFormService.getApiAppBookingFormGetList(undefined, 0, 100).catch(() => ({ items: [] }))
      ]);

      // BusinessBranding is a strict subset of CompanySettings, so derive the
      // branding fields from the company settings we already fetch rather than
      // making a second, redundant call to the branding endpoint.
      const brandingData: Routemize_DTOs_BusinessBrandingDTO = {
        businessName: companyData.businessName,
        businessWebsite: companyData.businessWebsite,
        // New tenants can be seeded with a junk bare-filename logo (e.g.
        // "logo-7641733172.jpeg"). Hide it here; the next save clears it
        // server-side since we persist the sanitized value.
        logoPath: isUsableLogoUrl(companyData.logoPath) ? companyData.logoPath : null,
        chatbotIconUrl: companyData.chatbotIconUrl,
        primaryColor: companyData.primaryColor,
        secondaryColor: companyData.secondaryColor,
        tertiaryColor: companyData.tertiaryColor,
        companyEmail: companyData.companyEmail,
      };
      setSettings({ ...brandingData, tertiaryColor: brandingData.tertiaryColor ?? '#f1f5f9' });
      setCompanySettings(companyData);
      
      // Count published booking forms
      const forms = bookingFormsData?.items || [];
      const activeCount = forms.filter((form: any) => form.status === Routemize_Constants_BookingFormStatus.PUBLISHED).length;
      setActiveBookingFormsCount(activeCount);
      
      // Initialize subdomain state
      if (companyData.subdomain) {
        setSubdomainInput(companyData.subdomain);
        setOriginalSubdomain(companyData.subdomain);
      }

      // Notify parent of initial data in inline mode
      if (inline && onDataChange) {
        onDataChange(brandingData);
      }
    } catch (error) {
      console.error('Failed to load business branding settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check subdomain availability with debounce
  const checkSubdomainAvailability = useCallback(async (subdomain: string) => {
    // Clear any existing timeout
    if (subdomainCheckTimeout.current) {
      clearTimeout(subdomainCheckTimeout.current);
    }

    // Don't check if same as original or empty
    if (!subdomain.trim()) {
      setSubdomainAvailability({ isAvailable: null, message: null, checking: false, normalizedSubdomain: null });
      return;
    }

    // Normalize for comparison (lowercase, alphanumeric only)
    const normalizedInput = subdomain.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedOriginal = originalSubdomain.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    if (normalizedInput === normalizedOriginal) {
      setSubdomainAvailability({ 
        isAvailable: true, 
        message: 'This is your current subdomain', 
        checking: false,
        normalizedSubdomain: normalizedInput 
      });
      return;
    }

    // Set checking state
    setSubdomainAvailability(prev => ({ ...prev, checking: true }));

    // Debounce the API call
    subdomainCheckTimeout.current = setTimeout(async () => {
      try {
        const result = await TenantRegistrationService.postApiAppTenantRegistrationCheckSubdomainAvailability(subdomain);
        setSubdomainAvailability({
          isAvailable: result.isAvailable ?? null,
          message: result.message ?? null,
          checking: false,
          normalizedSubdomain: result.normalizedSubdomain ?? null
        });
      } catch (error) {
        console.error('Failed to check subdomain availability:', error);
        setSubdomainAvailability({
          isAvailable: null,
          message: 'Failed to check availability. Please try again.',
          checking: false,
          normalizedSubdomain: null
        });
      }
    }, 500);
  }, [originalSubdomain]);

  // Handle subdomain input change
  const handleSubdomainChange = (value: string) => {
    // Only allow lowercase letters, numbers, and hyphens
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomainInput(sanitized);
    setSubdomainChangeAcknowledged(false);
    checkSubdomainAvailability(sanitized);
  };

  // Save subdomain change
  const handleSaveSubdomain = async () => {
    if (!companySettings?.id || !subdomainAvailability.isAvailable) return;
    
    try {
      setSavingSubdomain(true);
      setSubdomainSaveError(null);
      
      await CompanySettingsService.putApiAppCompanySettingsUpdate(companySettings.id, {
        subdomain: subdomainAvailability.normalizedSubdomain || subdomainInput
      });
      
      // Update local state
      const updatedSubdomain = subdomainAvailability.normalizedSubdomain || subdomainInput;
      setOriginalSubdomain(updatedSubdomain);
      setSubdomainInput(updatedSubdomain);
      setCompanySettings(prev => prev ? { ...prev, subdomain: updatedSubdomain } : null);
      setIsEditingSubdomain(false);
      setSubdomainChangeAcknowledged(false);
      
      // Show success feedback
      setAppliedField('subdomain');
      setTimeout(() => setAppliedField(null), 3000);
    } catch (error: any) {
      console.error('Failed to save subdomain:', error);
      setSubdomainSaveError(error?.message || 'Failed to save subdomain. Please try again.');
    } finally {
      setSavingSubdomain(false);
    }
  };

  // Cancel subdomain editing
  const handleCancelSubdomainEdit = () => {
    setSubdomainInput(originalSubdomain);
    setIsEditingSubdomain(false);
    setSubdomainChangeAcknowledged(false);
    setSubdomainAvailability({ isAvailable: null, message: null, checking: false, normalizedSubdomain: null });
    setSubdomainSaveError(null);
  };

  // Native <input type="color"> fires its change event continuously while the
  // user drags inside the OS picker. Each event previously triggered a full
  // setSettings -> re-render of this (very large) panel, which made the picker
  // feel laggy. We coalesce those rapid updates into a single state write per
  // animation frame so dragging stays smooth while the swatch still tracks live.
  const colorRafRef = useRef<Record<string, number | null>>({});
  const colorPendingRef = useRef<Record<string, string>>({});

  // Cleanup timeouts / pending color frames on unmount
  useEffect(() => {
    const colorFrames = colorRafRef.current;
    return () => {
      if (subdomainCheckTimeout.current) {
        clearTimeout(subdomainCheckTimeout.current);
      }
      Object.values(colorFrames).forEach((id) => {
        if (id) cancelAnimationFrame(id);
      });
    };
  }, []);

  const updateSetting = (field: keyof Routemize_DTOs_BusinessBrandingDTO, value: any) => {
    setSettings(prev => {
      const updated = { ...prev, [field]: value };

      // Notify parent of data changes in inline mode
      if (inline && onDataChange) {
        onDataChange(updated);
      }

      return updated;
    });
  };

  const handleColorChange = (field: string, value: string) => {
    updateSetting(field as keyof Routemize_DTOs_BusinessBrandingDTO, value);
    if (isValidHexColor(value)) {
      setColorErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleColorBlur = (field: string, value: string) => {
    if (!value || !value.trim()) {
      setColorErrors(prev => ({ ...prev, [field]: null }));
      return;
    }
    const normalized = normalizeHexColor(value);
    if (normalized) {
      updateSetting(field as keyof Routemize_DTOs_BusinessBrandingDTO, normalized);
      setColorErrors(prev => ({ ...prev, [field]: null }));
    } else {
      setColorErrors(prev => ({ ...prev, [field]: 'Enter a valid hex color (e.g., #FF5733)' }));
    }
  };

  // Throttled handler for the native color-swatch inputs. Stores the latest
  // value immediately and commits it at most once per animation frame, so a
  // drag that emits dozens of events per second only produces ~60 re-renders/s
  // worth of work instead of stacking up synchronous updates.
  const handleColorPickerInput = (field: keyof Routemize_DTOs_BusinessBrandingDTO, value: string) => {
    const key = field as string;
    colorPendingRef.current[key] = value;
    if (colorRafRef.current[key]) return;
    colorRafRef.current[key] = requestAnimationFrame(() => {
      colorRafRef.current[key] = null;
      updateSetting(field, colorPendingRef.current[key]);
      setColorErrors(prev => ({ ...prev, [key]: null }));
    });
  };

  const handleScrapeWebsite = async () => {
    const urlToScrape = settings.businessWebsite?.trim();
    if (!urlToScrape) return;
    
    try {
      setScrapingWebsite(true);
      setScrapeError(null);
      
      // Reset ALL previous scrape state before starting new scrape
      setWebsiteData(null);
      setLogoColorsExtracted(false);
      setExtractingLogoColors(false);
      setSelectedColorOrder([]);
      setAppliedField(null);
      setAnimateApplyButtons(false);
      
      const data = await fetchBrandAssets(urlToScrape);

      if (!data) {
        setScrapeError("That doesn't look like a website address. Try something like acmepainting.com.");
        return;
      }

      // The visible suggestion list is driven by the selected logo, so clear
      // the CSS-derived `colors` array. We deliberately keep
      // `cssColorFrequencies` on the state object — it's used to boost
      // logo colors that the site's stylesheet also uses, which is a strong
      // signal of an intentional brand color (vs. an incidental accent).
      data.colors = [];
      setWebsiteData(data);

      if (data.logos.length === 0) {
        setScrapeError('We could not find a logo for that address. Upload one below and pick your colors by hand.');
      } else if (data.isFallback) {
        // The icon track still found usable marks, so this is a partial win
        // rather than a failure — say so instead of implying nothing worked.
        setScrapeError('We could not read that site directly, so we pulled its icon instead. Colors need to be set by hand below.');
      }
    } catch (error) {
      console.error('Failed to fetch brand assets:', error);
      setScrapeError('Unable to load website data. Please check the URL and try again, or enter information manually.');
      setWebsiteData(null);
    } finally {
      setScrapingWebsite(false);
    }
  };

  const applyWebsiteData = (field: 'logo' | 'colors' | 'name', value?: string) => {
    if (!websiteData) return;

    // Set success feedback
    setAppliedField(field);
    setTimeout(() => setAppliedField(null), 2000);

    switch (field) {
      case 'logo':
        // If a specific logo URL is passed, use that; otherwise use the primary logo
        const logoToApply = value || websiteData.logo || websiteData.favicon;
        if (logoToApply) {
          clearPendingLogoPreview();
          updateSetting('logoPath', logoToApply);

          // Use the top CSS colors as a boost signal — colors authored
          // repeatedly in the stylesheet are very likely intentional brand
          // colors, so we want logo regions matching them to outrank
          // incidental accents.
          const boostHexes = topCssBoostHexes(websiteData.cssColorFrequencies);

          // Extract colors from the selected logo and update brand color options
          setExtractingLogoColors(true);
          setLogoColorsExtracted(false);
          setSelectedColorOrder([]);
          extractColorsFromImage(logoToApply, { boostHexes }).then(logoColors => {
            setExtractingLogoColors(false);
            if (logoColors.length > 0) {
              const reordered = sortCssConfirmedFirst(logoColors, boostHexes);
              setWebsiteData(prev => prev ? { ...prev, colors: reordered } : prev);
              setLogoColorsExtracted(true);
            } else {
              // Logo pixel extraction failed — most commonly because the logo
              // is hosted behind CORS that every proxy refused. Fall back to
              // the colors we scraped from the site's CSS so the user still
              // gets brand color suggestions instead of an empty state.
              setWebsiteData(prev => {
                if (!prev) return prev;
                const fallback = prev.cssColors && prev.cssColors.length > 0
                  ? prev.cssColors
                  : prev.colors;
                return { ...prev, colors: fallback };
              });
              setLogoColorsExtracted(true);
            }
          });
        }
        break;
      case 'colors':
        if (value) {
          // User clicked a specific color - set it as primary
          const selectedIndex = websiteData.colors.indexOf(value);
          const otherColors = websiteData.colors.filter((c, idx) => idx !== selectedIndex);
          
          // Update all colors in a single state update
          setSettings(prev => ({
            ...prev,
            primaryColor: value,
            secondaryColor: otherColors[0] || prev.secondaryColor,
            tertiaryColor: otherColors[1] || prev.tertiaryColor
          }));

          // Notify parent in inline mode
          if (inline && onDataChange) {
            onDataChange({
              ...settings,
              primaryColor: value,
              secondaryColor: otherColors[0] || settings.secondaryColor,
              tertiaryColor: otherColors[1] || settings.tertiaryColor
            });
          }
        } else if (websiteData.colors.length > 0) {
          // Apply all colors in order - single state update
          setSettings(prev => ({
            ...prev,
            primaryColor: websiteData.colors[0],
            secondaryColor: websiteData.colors[1] || prev.secondaryColor,
            tertiaryColor: websiteData.colors[2] || prev.tertiaryColor
          }));

          // Notify parent in inline mode
          if (inline && onDataChange) {
            onDataChange({
              ...settings,
              primaryColor: websiteData.colors[0],
              secondaryColor: websiteData.colors[1] || settings.secondaryColor,
              tertiaryColor: websiteData.colors[2] || settings.tertiaryColor
            });
          }
        }
        // Don't break - let it fall through to the forced re-render
        return; // Return instead of break to skip the generic setSettings at the end
      case 'name':
        // Use businessName (better parsed) or fall back to title
        const nameToApply = websiteData.businessName || websiteData.title;
        if (nameToApply) {
          const newSettings = {
            ...settings,
            businessName: nameToApply
          };
          setSettings(newSettings);
          
          if (inline && onDataChange) {
            onDataChange(newSettings);
          }
        }
        break;
    }
    
    // Force a re-render by creating a new object
    setSettings(prev => ({ ...prev }));
  };

  // Handle manual logo file upload
  const handleManualLogoUpload = async (file: File): Promise<string | null> => {
    try {
      setUploadingManualLogo(true);
      setLogoUploadError(null);
      
      // Get company settings to use the ID
      const companySettings = await CompanySettingsService.getApiAppCompanySettingsGet();
      if (!companySettings.id) {
        throw new Error('Company settings ID not found');
      }

      // Upload to S3
      await AttachmentsService.postApiAppAttachmentsUploadFiles(
        companySettings.id,
        'BusinessBranding',
        { Files: [sanitizeFileForUpload(file)] }
      );

      // Retrieve the uploaded file info to get the S3 URL
      const attachments = await AttachmentsService.getApiAppAttachmentsGetAttachments(
        companySettings.id,
        'BusinessBranding'
      );

      // Get the most recent attachment (should be the one we just uploaded)
      if (attachments && attachments.length > 0) {
        const uploadedLogo = attachments[attachments.length - 1];
        const s3Url = uploadedLogo.filePath;
        
        // Update settings with S3 URL immediately
        updateSetting('logoPath', s3Url || '');
        
        return s3Url || null;
      }
      
      throw new Error('Failed to retrieve uploaded logo URL');
      
    } catch (error) {
      console.error('Error uploading manual logo:', error);
      setLogoUploadError('Failed to upload logo. Please try again or use a different image.');
      throw error;
    } finally {
      setUploadingManualLogo(false);
    }
  };

  // Client-side validation for the chatbot icon (mirrors the logo's constraints).
  const CHATBOT_ICON_ALLOWED_TYPES = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp',
  ];
  const CHATBOT_ICON_ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
  const CHATBOT_ICON_MAX_BYTES = 5 * 1024 * 1024;

  const validateChatbotIconFile = (file: File): string | null => {
    if (file.size === 0) return 'That file appears to be empty. Please choose a different image.';
    if (file.size > CHATBOT_ICON_MAX_BYTES) {
      return `Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Max file size is 5 MB.`;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const typeAllowed = file.type
      ? CHATBOT_ICON_ALLOWED_TYPES.includes(file.type.toLowerCase())
      : CHATBOT_ICON_ALLOWED_EXTS.includes(ext);
    if (!typeAllowed) {
      const detected = file.type || (ext ? `.${ext}` : 'unknown');
      return `That file type (${detected}) isn't supported. Please upload JPG, PNG, GIF, SVG, or WebP.`;
    }
    return null;
  };

  // Upload a chatbot avatar icon to S3. It uses its own 'ChatbotIcon' attachment
  // category (keyed by the company settings id) so it never co-mingles with the
  // company logo, which lives under 'BusinessBranding'. The new attachment is
  // resolved by diffing ids against the pre-upload set rather than picking the
  // "last" item — GetAttachments does not order its results, so index-based
  // selection can return the wrong file.
  const handleChatbotIconUpload = async (file: File): Promise<void> => {
    const validationError = validateChatbotIconFile(file);
    if (validationError) {
      setChatbotIconUploadError(validationError);
      return;
    }
    try {
      setUploadingChatbotIcon(true);
      setChatbotIconUploadError(null);

      const companyData = await CompanySettingsService.getApiAppCompanySettingsGet();
      if (!companyData.id) {
        throw new Error('Company settings ID not found');
      }

      const before = await AttachmentsService.getApiAppAttachmentsGetAttachments(
        companyData.id,
        'ChatbotIcon'
      );
      const beforeIds = new Set((before ?? []).map(a => a.id));

      await AttachmentsService.postApiAppAttachmentsUploadFiles(
        companyData.id,
        'ChatbotIcon',
        { Files: [sanitizeFileForUpload(file)] }
      );

      const after = await AttachmentsService.getApiAppAttachmentsGetAttachments(
        companyData.id,
        'ChatbotIcon'
      );

      const uploaded = (after ?? []).find(a => !beforeIds.has(a.id));
      if (!uploaded?.filePath) {
        throw new Error('Failed to retrieve the uploaded icon URL');
      }

      updateSetting('chatbotIconUrl', uploaded.filePath);
    } catch (error) {
      console.error('Error uploading chatbot icon:', error);
      setChatbotIconUploadError('Failed to upload the icon. Please try again or use a different image.');
    } finally {
      setUploadingChatbotIcon(false);
    }
  };

  // Validate a logo file, extract brand colors from it, and upload it.
  // Shared by the file-picker input and the drag-and-drop zone.
  const processLogoFile = async (file: File) => {
    setLogoUploadError(null);

    const ALLOWED_LABEL = 'JPG, PNG, GIF, SVG, or WebP';
    const ALLOWED_TYPES = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/svg+xml',
      'image/webp',
    ];
    const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
    const fileExt = (file.name.split('.').pop() || '').toLowerCase();

    if (file.size > 5 * 1024 * 1024) {
      const msg = `Logo is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Max file size is 5 MB.`;
      setLogoUploadError(msg);
      showToast(msg, 'error');
      return;
    }

    const typeAllowed = file.type
      ? ALLOWED_TYPES.includes(file.type.toLowerCase())
      : ALLOWED_EXTS.includes(fileExt);
    if (!typeAllowed) {
      const detected = file.type || (fileExt ? `.${fileExt}` : 'unknown');
      const msg = `That file type (${detected}) isn't supported. Please upload ${ALLOWED_LABEL}.`;
      setLogoUploadError(msg);
      showToast(msg, 'error');
      return;
    }

    if (file.size === 0) {
      const msg = 'That file appears to be empty. Please choose a different image.';
      setLogoUploadError(msg);
      showToast(msg, 'error');
      return;
    }

    // Show the local file immediately in the preview area so the user gets
    // instant visual feedback while the upload is in flight.
    replacePendingLogoPreview(URL.createObjectURL(file));
    setLogoJustUploaded(false);
    if (logoUploadedTimeoutRef.current) clearTimeout(logoUploadedTimeoutRef.current);

    // Extract colors from the local file before uploading.
    // If the user previously scraped their website we reuse those CSS-derived
    // colors as a boost signal so logo accents that don't match the site
    // stylesheet are de-emphasized.
    const localUrl = URL.createObjectURL(file);
    const uploadBoostHexes = topCssBoostHexes(websiteData?.cssColorFrequencies);
    extractColorsFromImage(localUrl, { boostHexes: uploadBoostHexes }).then(logoColors => {
      URL.revokeObjectURL(localUrl);
      if (logoColors.length > 0) {
        const reordered = sortCssConfirmedFirst(logoColors, uploadBoostHexes);
        // Use functional update to avoid stale closure overwriting logoPath
        // set by the concurrent handleManualLogoUpload call
        setSettings(prev => {
          const updated = {
            ...prev,
            primaryColor: reordered[0],
            secondaryColor: reordered[1] || prev.secondaryColor,
            tertiaryColor: reordered[2] || prev.tertiaryColor
          };
          if (inline && onDataChange) {
            onDataChange(updated);
          }
          return updated;
        });
        setAppliedField('colors');
        setTimeout(() => setAppliedField(null), 3000);
      }
    });

    try {
      await handleManualLogoUpload(file);
      showToast('Logo uploaded successfully!', 'success');
      setLogoJustUploaded(true);
      logoUploadedTimeoutRef.current = setTimeout(() => setLogoJustUploaded(false), 4000);
      // Keep the local preview on screen — it's pixel-identical to the
      // uploaded file and avoids an empty flash while the CDN URL loads.
    } catch {
      setLogoUploadError('Failed to upload logo. Please try again or use a different image.');
      clearPendingLogoPreview();
    }
  };

  // Download logo from external URL and upload to S3
  const downloadAndUploadLogo = async (logoUrl: string): Promise<string | null> => {
    try {
      // Fetch the logo from external URL
      const response = await fetch(logoUrl);
      if (!response.ok) {
        throw new Error(`Failed to download logo: ${response.statusText}`);
      }
      
      // Convert to blob
      const blob = await response.blob();
      
      // Extract filename from URL or generate one
      const urlParts = logoUrl.split('/');
      const originalFilename = urlParts[urlParts.length - 1].split('?')[0] || 'logo.png';
      
      // Determine file extension and type
      const extension = originalFilename.split('.').pop()?.toLowerCase() || 'png';
      const mimeTypes: { [key: string]: string } = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'svg': 'image/svg+xml',
        'webp': 'image/webp'
      };
      const mimeType = mimeTypes[extension] || blob.type || 'image/png';
      
      // Create a File object from the blob
      const filename = `logo-${Date.now()}.${extension}`;
      const file = new File([blob], filename, { type: mimeType });

      // Get company settings to use the ID
      const companySettings = await CompanySettingsService.getApiAppCompanySettingsGet();
      if (!companySettings.id) {
        throw new Error('Company settings ID not found');
      }

      // Upload to S3
      await AttachmentsService.postApiAppAttachmentsUploadFiles(
        companySettings.id,
        'BusinessBranding',
        { Files: [file] }
      );
      
      // Retrieve the uploaded file info to get the S3 URL
      const attachments = await AttachmentsService.getApiAppAttachmentsGetAttachments(
        companySettings.id,
        'BusinessBranding'
      );
      
      // Get the most recent attachment (should be the one we just uploaded)
      if (attachments && attachments.length > 0) {
        const uploadedLogo = attachments[attachments.length - 1];
        const s3Url = uploadedLogo.filePath;
        return s3Url || null;
      }
      
      throw new Error('Failed to retrieve uploaded logo URL');
      
    } catch (error) {
      console.error('Error downloading/uploading logo:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!settings.businessName?.trim()) {
      setNameError('Business name is required');
      return;
    }
    setNameError(null);

    let saveSettings = { ...settings };
    const colorFields = ['primaryColor', 'secondaryColor', 'tertiaryColor'] as const;
    for (const field of colorFields) {
      const val = saveSettings[field];
      if (val && !isValidHexColor(val)) {
        const normalized = normalizeHexColor(val);
        if (normalized) {
          saveSettings = { ...saveSettings, [field]: normalized };
          setSettings(prev => ({ ...prev, [field]: normalized }));
        } else {
          setColorErrors(prev => ({ ...prev, [field]: 'Enter a valid hex color (e.g., #FF5733)' }));
          return;
        }
      }
    }

    try {
      setSaving(true);

      // If logo is an external URL, download and upload to S3 first
      if (saveSettings.logoPath && saveSettings.logoPath.startsWith('http')) {
        try {
          setUploadingLogo(true);
          const s3Url = await downloadAndUploadLogo(saveSettings.logoPath);

          if (s3Url) {
            saveSettings = { ...saveSettings, logoPath: s3Url };
          }
        } catch (logoError) {
          console.error('Failed to upload logo to S3:', logoError);
          // Show error but allow user to continue saving other settings
          setScrapeError('Failed to upload logo to S3. Logo will be saved as external URL.');
          // Continue with save even if logo upload fails
        } finally {
          setUploadingLogo(false);
        }
      }

      await BusinessBrandingService.putApiAppBusinessBrandingUpdate(saveSettings);
      
      // Reload settings to show the updated data (including S3 logo URL)
      await loadSettings();
      
      // Clear any previous errors
      setScrapeError(null);
      
      // Show success toast
      showToast('Branding settings saved successfully!', 'success');
      
      // Don't close the modal - keep it open to show the updated preview
      // if (!inline) {
      //   onClose();
      // }
    } catch (error) {
      console.error('Failed to save business branding settings:', error);
      setScrapeError('Failed to save branding settings. Please try again.');
      throw error; // Re-throw for onSave callback to handle
    } finally {
      setSaving(false);
    }
  };

  // Expose save function via window for inline mode
  useEffect(() => {
    if (inline) {
      (window as any).__enhancedBusinessBrandingSave = handleSave;
    }
    return () => {
      if (inline) {
        delete (window as any).__enhancedBusinessBrandingSave;
      }
    };
  }, [inline, handleSave]);

  if (!isOpen) return null;

  // Content section (shared between modal and inline modes)
  const contentSection = (
    <div className={inline ? "px-4 py-3" : "flex-1 overflow-y-auto p-4 sm:p-6 min-h-0"}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink-900"></div>
              </div>
            ) : (
              <div className={inline ? "flex flex-col gap-4" : "space-y-4 sm:space-y-6"}>
                {/* My Subdomain Settings - hidden in inline/setup guide mode */}
                {!inline && <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-xl p-5 border border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Link2 className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-ink-900">My Subdomain</h4>
                        <p className="text-xs text-ink-500">Your custom booking URL</p>
                      </div>
                    </div>
                    {appliedField === 'subdomain' && (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Saved!
                      </span>
                    )}
                  </div>

                  {/* Current subdomain display */}
                  {!isEditingSubdomain ? (
                    <div className="space-y-3">
                      <div className="bg-surface rounded-lg p-4 border border-blue-200">
                        <label className="text-xs text-ink-500 mb-1 block">Your Booking URL</label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm font-medium text-blue-700 bg-blue-50 px-3 py-2 rounded-md">
                            {originalSubdomain ? `${originalSubdomain}.routemize.com` : 'Not configured'}
                          </code>
                          <Button variant="link" size="sm" onClick={() => setIsEditingSubdomain(true)} className="gap-1.5">
                            <Edit3 className="w-4 h-4" />
                            Edit
                          </Button>
                        </div>
                        {originalSubdomain && (
                          <p className="text-xs text-ink-500 mt-2">
                            Customers can access your booking forms at this URL
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Subdomain input */}
                      <div className="bg-surface rounded-lg p-4 border border-blue-200">
                        <label className="text-xs text-ink-600 mb-2 block font-medium">Enter your desired subdomain</label>
                        <div className="flex items-center">
                          <input
                            type="text"
                            value={subdomainInput}
                            onChange={(e) => handleSubdomainChange(e.target.value)}
                            className={`flex-1 px-3 py-2 border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                              subdomainAvailability.isAvailable === false 
                                ? 'border-red-300 bg-red-50' 
                                : subdomainAvailability.isAvailable === true 
                                  ? 'border-green-300 bg-green-50' 
                                  : 'border-ink-300'
                            }`}
                            placeholder="yourcompany"
                          />
                          <span className="px-3 py-2 bg-ink-100 border border-l-0 border-ink-300 rounded-r-lg text-sm text-ink-600">
                            .routemize.com
                          </span>
                        </div>

                        {/* Availability status */}
                        <div className="mt-2 min-h-[20px]">
                          {subdomainAvailability.checking ? (
                            <div className="flex items-center gap-2 text-xs text-ink-500">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Checking availability...
                            </div>
                          ) : subdomainAvailability.isAvailable === true ? (
                            <div className="flex items-center gap-2 text-xs text-green-600">
                              <CheckCircle2 className="w-3 h-3" />
                              {subdomainAvailability.message || 'Subdomain is available!'}
                            </div>
                          ) : subdomainAvailability.isAvailable === false ? (
                            <div className="flex items-center gap-2 text-xs text-red-600">
                              <XCircle className="w-3 h-3" />
                              {subdomainAvailability.message || 'Subdomain is not available'}
                            </div>
                          ) : subdomainAvailability.message ? (
                            <div className="flex items-center gap-2 text-xs text-amber-600">
                              <AlertTriangle className="w-3 h-3" />
                              {subdomainAvailability.message}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Warning about URL changes - only show if changing from an existing subdomain */}
                      {subdomainAvailability.isAvailable && subdomainInput !== originalSubdomain && originalSubdomain && (
                        <div className={`${activeBookingFormsCount > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4`}>
                          <div className="flex gap-3">
                            <AlertTriangle className={`w-5 h-5 ${activeBookingFormsCount > 0 ? 'text-red-500' : 'text-amber-500'} flex-shrink-0 mt-0.5`} />
                            <div className="space-y-2">
                              {activeBookingFormsCount > 0 ? (
                                <>
                                  <p className="text-sm font-medium text-red-800">
                                    Warning: {activeBookingFormsCount} Active Booking {activeBookingFormsCount === 1 ? 'Form' : 'Forms'} Will Be Affected
                                  </p>
                                  <p className="text-xs text-red-700">
                                    You currently have <strong>{activeBookingFormsCount} active booking {activeBookingFormsCount === 1 ? 'form' : 'forms'}</strong> linked 
                                    to your current subdomain. Changing your subdomain will <strong>break all existing links</strong> to 
                                    these forms. Any shared URLs, QR codes, or embedded forms will no longer work.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm font-medium text-amber-800">Important: URL Change Warning</p>
                                  <p className="text-xs text-amber-700">
                                    Changing your subdomain will update all of your booking form URLs. Any existing links, 
                                    QR codes, or embedded forms will stop working and need to be updated.
                                  </p>
                                </>
                              )}
                              <ul className={`text-xs ${activeBookingFormsCount > 0 ? 'text-red-700' : 'text-amber-700'} list-disc list-inside space-y-1`}>
                                <li>Current URL: <code className={`${activeBookingFormsCount > 0 ? 'bg-red-100' : 'bg-amber-100'} px-1 rounded`}>{originalSubdomain}.routemize.com</code></li>
                                <li>New URL: <code className={`${activeBookingFormsCount > 0 ? 'bg-red-100' : 'bg-amber-100'} px-1 rounded`}>{subdomainAvailability.normalizedSubdomain || subdomainInput}.routemize.com</code></li>
                              </ul>

                              <p className={`text-xs ${activeBookingFormsCount > 0 ? 'text-red-700' : 'text-amber-700'} mt-2`}>
                                You will need to <strong>log out and log back in</strong> after saving for the change to take effect in your current session.
                              </p>

                              {/* Acknowledgment checkbox */}
                              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={subdomainChangeAcknowledged}
                                  onChange={(e) => setSubdomainChangeAcknowledged(e.target.checked)}
                                  className="mt-0.5 h-4 w-4 text-blue-600 border-ink-300 rounded focus:ring-blue-500"
                                />
                                <span className={`text-xs ${activeBookingFormsCount > 0 ? 'text-red-800' : 'text-amber-800'} font-medium`}>
                                  {activeBookingFormsCount > 0
                                    ? `I understand that my ${activeBookingFormsCount} active booking ${activeBookingFormsCount === 1 ? 'form' : 'forms'} will have new URLs and all previously shared links will stop working`
                                    : 'I understand that all existing form URLs will change and I\'ll need to update any shared links'
                                  }
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* First time setup info - show when no original subdomain exists */}
                      {subdomainAvailability.isAvailable && !originalSubdomain && subdomainInput && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-green-800">Your Booking URL</p>
                              <p className="text-xs text-green-700">
                                Once saved, customers will be able to access your booking forms at:
                              </p>
                              <code className="block text-sm font-medium text-green-800 bg-green-100 px-3 py-2 rounded-md">
                                {subdomainAvailability.normalizedSubdomain || subdomainInput}.routemize.com
                              </code>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Error message */}
                      {subdomainSaveError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-700">{subdomainSaveError}</p>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex items-center justify-end gap-3">
                        <Button variant="secondary" onClick={handleCancelSubdomainEdit}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveSubdomain}
                          disabled={
                            !subdomainAvailability.isAvailable ||
                            !subdomainInput.trim() ||
                            !!(subdomainInput !== originalSubdomain && originalSubdomain && !subdomainChangeAcknowledged)
                          }
                          loading={savingSubdomain}
                        >
                          {savingSubdomain ? 'Saving...' : (
                            <>
                              <Check className="w-4 h-4" />
                              Save Subdomain
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Help text */}
                  <div className="mt-4 pt-4 border-t border-blue-100">
                    <p className="text-xs text-ink-600">
                      <span className="font-medium">💡 Tip:</span> Choose a subdomain that's easy to remember and matches your business name. 
                      Use only lowercase letters, numbers, and hyphens.
                    </p>
                  </div>
                </div>}

                {/* Business Name — first during onboarding. It is the only
                    required field here, and ordering it after the website
                    scraper and logo grid pushed it off the first screen. */}
                <div className={inline ? 'order-1' : ''}>
                  <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                    <Building className="h-4 w-4" />
                    Business Name
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={settings.businessName || ''}
                    onChange={(e) => {
                      updateSetting('businessName', e.target.value);
                      if (e.target.value.trim()) {
                        setNameError(null);
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${nameError ? 'border-red-500' : 'border-ink-300'}`}
                    placeholder="Enter your business name"
                    required
                  />
                  {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                </div>

                {/* Company Email — hidden during onboarding (inline mode). The
                    Reply-To address is non-essential up front and was clutter
                    here; users configure it later from full Settings. The save
                    handler only requires businessName so omitting this input
                    doesn't block progression. */}
                {!inline && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                      <Mail className="h-4 w-4" />
                      Company Email
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={settings.companyEmail || ''}
                      onChange={(e) => updateSetting('companyEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="contact@yourbusiness.com"
                      required
                    />
                    <p className="mt-1.5 text-xs text-ink-500">
                      This is used as the Reply-To address on customer emails (confirmations, reminders, etc.)
                    </p>
                  </div>
                )}

                {/* Business Phone — hidden during onboarding (inline mode).
                    Drives the area code used to provision Voice Agent numbers,
                    so the field is required before enabling that feature. */}
                {!inline && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                      <Phone className="h-4 w-4" />
                      Business Phone
                    </label>
                    <input
                      type="tel"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      onBlur={async () => {
                        if (!companySettings?.id) return;
                        // Normalize via the shared formatter so storage and display agree.
                        const formatted = formatPhoneNumber(phoneDraft);
                        if (formatted !== phoneDraft) setPhoneDraft(formatted);
                        const currentFormatted = formatPhoneNumber(companySettings.phone ?? '');
                        if (formatted === currentFormatted) return;
                        try {
                          setSavingPhone(true);
                          const updated = await CompanySettingsService.putApiAppCompanySettingsUpdate(companySettings.id, {
                            phone: formatted,
                          });
                          setCompanySettings(updated);
                          showToast('Business phone updated.', 'success');
                        } catch (error) {
                          console.error('Failed to save business phone:', error);
                          showToast('Failed to update business phone.', 'info');
                        } finally {
                          setSavingPhone(false);
                        }
                      }}
                      disabled={savingPhone}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      placeholder="(555) 555-5555"
                    />
                    {voiceAgentAccess.available && (
                      <p className="mt-1.5 text-xs text-ink-500">
                        Used to determine your area code when provisioning Voice Agent phone numbers.
                      </p>
                    )}
                  </div>
                )}

                {/* Country — drives ZIP/postal + State/Province labels, validation,
                    distance units, and service-area keying. Hidden during onboarding. */}
                {!inline && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                      <Globe className="h-4 w-4" />
                      Country
                    </label>
                    <select
                      value={companySettings?.country || 'US'}
                      onChange={async (e) => {
                        const newCountry = e.target.value;
                        if (!companySettings?.id || newCountry === (companySettings.country || 'US')) return;
                        try {
                          setSavingCountry(true);
                          const updated = await CompanySettingsService.putApiAppCompanySettingsUpdate(companySettings.id, {
                            country: newCountry,
                          });
                          setCompanySettings(updated);
                          // Propagate to the shared context so labels/units/validation update app-wide.
                          await refreshCompanySettings();
                          showToast('Country updated successfully!', 'success');
                        } catch (error) {
                          console.error('Failed to save country:', error);
                          showToast('Failed to update country.', 'error');
                        } finally {
                          setSavingCountry(false);
                        }
                      }}
                      disabled={savingCountry}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface disabled:opacity-50"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                    <p className="mt-1.5 text-xs text-ink-500">
                      Sets address format (ZIP/Postal Code, State/Province), distance units, and service-area matching.
                    </p>
                  </div>
                )}

                {/* Timezone — hidden during onboarding (inline mode). The
                    backend default works for most users; full settings still
                    surfaces this control. */}
                {!inline && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                      <ClockIcon className="h-4 w-4" />
                      Timezone
                    </label>
                    <select
                      value={companySettings?.timeZone || ''}
                      onChange={async (e) => {
                        const newTz = e.target.value;
                        if (!companySettings?.id) return;
                        try {
                          setSavingTimezone(true);
                          const updated = await CompanySettingsService.putApiAppCompanySettingsUpdate(companySettings.id, {
                            timeZone: newTz,
                          });
                          setCompanySettings(updated);
                          showToast('Timezone updated successfully!', 'success');
                        } catch (error) {
                          console.error('Failed to save timezone:', error);
                          showToast('Failed to update timezone.', 'info');
                        } finally {
                          setSavingTimezone(false);
                        }
                      }}
                      disabled={savingTimezone}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface disabled:opacity-50"
                    >
                      <option value="America/New_York">(GMT-5:00) Eastern Time (US &amp; Canada)</option>
                      <option value="America/Toronto">(GMT-5:00) Eastern Time - Toronto (Canada)</option>
                      <option value="America/Chicago">(GMT-6:00) Central Time (US &amp; Canada)</option>
                      <option value="America/Winnipeg">(GMT-6:00) Central Time - Winnipeg (Canada)</option>
                      <option value="America/Denver">(GMT-7:00) Mountain Time (US &amp; Canada)</option>
                      <option value="America/Edmonton">(GMT-7:00) Mountain Time - Edmonton (Canada)</option>
                      <option value="America/Los_Angeles">(GMT-8:00) Pacific Time (US &amp; Canada)</option>
                      <option value="America/Vancouver">(GMT-8:00) Pacific Time - Vancouver (Canada)</option>
                      <option value="America/Halifax">(GMT-4:00) Atlantic Time - Halifax (Canada)</option>
                      <option value="America/St_Johns">(GMT-3:30) Newfoundland Time - St. John&#39;s (Canada)</option>
                      <option value="America/Anchorage">(GMT-9:00) Alaska</option>
                      <option value="Pacific/Honolulu">(GMT-10:00) Hawaii</option>
                    </select>
                    <p className="mt-1.5 text-xs text-ink-500">
                      Used for scheduling, notifications, and appointment times
                    </p>
                  </div>
                )}

                {/* Website URL */}
                <div className={inline ? 'order-2' : ''}>
                  {inline && (
                    <div className="mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-ink-900">Start with your website</span>
                      <span className="text-xs text-ink-500">— we'll pull in your logo & colors</span>
                    </div>
                  )}
                  {!inline && (
                    <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                      <Globe className="h-4 w-4" />
                      Website URL
                    </label>
                  )}
                  <div className={inline ? "" : "space-y-3"}>
                    <div className={inline ? "flex flex-col md:flex-row gap-2 min-w-0" : "flex gap-2 min-w-0"}>
                      <div className={inline ? "flex-1 min-w-0 flex gap-2" : "flex-1 min-w-0 flex gap-2"}>
                        <input
                          type="text"
                          value={settings.businessWebsite || ''}
                          onChange={(e) => updateSetting('businessWebsite', e.target.value)}
                          onBlur={(e) => {
                            const value = e.target.value.trim();
                            if (value && !value.match(/^https?:\/\//i)) {
                              updateSetting('businessWebsite', 'https://' + value);
                            }
                          }}
                          className="flex-1 min-w-0 px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface"
                          placeholder="https://www.yourbusiness.com"
                        />
                        {settings.businessWebsite && (
                          <a
                            href={settings.businessWebsite}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 flex items-center px-3 py-2 text-ink-500 hover:text-ink-700 border border-ink-300 rounded-lg hover:bg-ink-50 bg-surface"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                      {inline && (
                        // eslint-disable-next-line no-restricted-syntax -- Brand gradient CTA (blue-600 → indigo-600); intentional AI-feature visual treatment, doesn't fit Button variants
                        <button
                          onClick={async () => {
                            setWebsiteData(null);
                            setShowWebsiteScraper(true);
                            await handleScrapeWebsite();
                          }}
                          disabled={scrapingWebsite || !settings.businessWebsite}
                          className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-500 hover:to-indigo-500 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {scrapingWebsite ? (
                            <>
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Fetching...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4" />
                              Fetch Brand Assets
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Standalone fetch button (modal mode) */}
                    {!inline && settings.businessWebsite && (
                      <div className="space-y-2">
                        <Button
                          onClick={async () => {
                            setWebsiteData(null);
                            setShowWebsiteScraper(true);
                            await handleScrapeWebsite();
                          }}
                          loading={scrapingWebsite}
                        >
                          {scrapingWebsite ? 'Scanning website...' : (
                            <>
                              <Download className="h-4 w-4" />
                              Fetch Brand Assets
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Error message with graceful fallback hint */}
                    {scrapeError && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs text-amber-800">{scrapeError}</p>
                          <p className="text-11 text-amber-700 mt-1">
                            No worries — you can upload your logo manually below.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Auto-fill results */}
                    {showWebsiteScraper && websiteData && (
                      <div className={`${websiteData.isFallback ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
                        <div className="mb-3">
                          <h4 className={`text-sm font-medium ${websiteData.isFallback ? 'text-amber-900' : 'text-blue-900'}`}>
                            {websiteData.isFallback ? 'Limited Data Found' : 'Found Data from Website'}
                          </h4>
                        </div>
                        
                        {/* Logos Selection - Show top 3 found logos */}
                        {websiteData.logos && websiteData.logos.length > 0 && (() => {
                          // Prioritize "logo" > "icon" > "og-image" > "favicon" types, then take top 3
                          const typeOrder: Record<string, number> = { logo: 0, icon: 1, 'og-image': 2, favicon: 3 };
                          const topLogos = [...websiteData.logos]
                            .sort((a, b) => (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99))
                            .slice(0, 3);
                          return (
                            <div className="bg-surface rounded-lg p-3 mb-2">
                              <div className="flex items-center gap-2 mb-3">
                                <Image className="h-4 w-4 text-ink-500" />
                                <span className="text-sm text-ink-700 font-medium">
                                  {topLogos.length === 1 ? 'Logo Found' : `Top ${topLogos.length} Logos Found`}
                                </span>
                                {appliedField === 'logo' && (
                                  <span className="text-xs text-green-600 font-medium">✓ Applied!</span>
                                )}
                              </div>
                              <p className="text-xs text-ink-600 mb-3">
                                {topLogos.length > 1
                                  ? 'Pick the logo you want to use.'
                                  : 'Click to apply this logo.'}
                              </p>
                              <LogoGrid
                                logos={topLogos}
                                selectedUrl={settings.logoPath}
                                animateApplyButtons={animateApplyButtons}
                                onSelect={(url) => applyWebsiteData('logo', url)}
                              />
                            </div>
                          );
                        })()}
                        
                        {/* Colors - only shown after a logo is selected */}
                        <div className="bg-surface rounded-lg p-3 mb-2">
                          <div className="flex items-center gap-2 mb-3">
                            <Palette className="h-4 w-4 text-ink-500" />
                            <span className="text-sm text-ink-700 font-medium">Brand Colors</span>
                          </div>
                          
                          {extractingLogoColors ? (
                            // Extracting colors from selected logo
                            <div className="flex items-center gap-3 py-4 justify-center">
                              <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
                              <span className="text-sm text-ink-600">Extracting colors from logo...</span>
                            </div>
                          ) : !logoColorsExtracted ? (
                            // No logo selected yet
                            <div className="flex flex-col items-center gap-2 py-4 text-center">
                              <div className="w-10 h-10 rounded-full bg-ink-100 flex items-center justify-center">
                                <Palette className="h-5 w-5 text-ink-400" />
                              </div>
                              <p className="text-sm text-ink-500">Select a logo above to extract brand colors</p>
                              <p className="text-xs text-ink-400">Colors will be pulled from the logo image automatically</p>
                            </div>
                          ) : websiteData.colors.length > 0 ? (
                            // Colors extracted — show the palette
                            <>
                              <p className="text-xs text-ink-600 mb-2">Click colors in order for Primary (1), Secondary (2), Tertiary (3):</p>
                              <div className="flex gap-2 flex-wrap">
                                {websiteData.colors.map((color, index) => {
                                  const selectionIndex = selectedColorOrder.indexOf(color);
                                  const isSelected = selectionIndex !== -1;
                                  const orderNumber = isSelected ? selectionIndex + 1 : null;
                                  
                                  return (
                                    // eslint-disable-next-line no-restricted-syntax -- Color swatch picker with inline-style background; order-number badge overlays the swatch when selected. Not a Button shape.
                                    <button
                                      key={index}
                                      onClick={() => {
                                        setSelectedColorOrder(prev => {
                                          if (prev.includes(color)) {
                                            return prev.filter(c => c !== color);
                                          }
                                          if (prev.length >= 3) {
                                            return [...prev.slice(1), color];
                                          }
                                          return [...prev, color];
                                        });
                                      }}
                                      className={`group relative flex flex-col items-center gap-1 px-3 py-2 rounded-lg border-2 transition-all ${
                                        isSelected 
                                          ? 'border-blue-500 bg-blue-50' 
                                          : 'border-ink-200 hover:border-blue-400 hover:bg-blue-50'
                                      }`}
                                      title={isSelected ? `Selected as #${orderNumber}` : 'Click to select'}
                                    >
                                      <div
                                        className="w-8 h-8 rounded-md shadow-sm"
                                        style={{ backgroundColor: color }}
                                      ></div>
                                      <span className={`text-2xs font-mono font-semibold ${
                                        isSelected ? 'text-blue-700' : 'text-ink-600 group-hover:text-blue-700'
                                      }`}>
                                        {color}
                                      </span>
                                      {orderNumber === 1 && (
                                        <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-9 px-1 rounded-full pointer-events-none font-bold">1</span>
                                      )}
                                      {orderNumber === 2 && (
                                        <span className="absolute -top-1 -right-1 bg-green-500 text-white text-9 px-1 rounded-full pointer-events-none font-bold">2</span>
                                      )}
                                      {orderNumber === 3 && (
                                        <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-9 px-1 rounded-full pointer-events-none font-bold">3</span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="mt-3 pt-3 border-t border-ink-100">
                                <p className="text-11 text-ink-500">
                                  💡 <strong>Click colors in order:</strong> 1st click = Primary, 2nd = Secondary, 3rd = Tertiary. Click again to deselect.
                                </p>
                                {/* Apply Colors CTA — promoted to a full-width
                                    primary button so it's impossible to miss
                                    after the user picks colors. The previous
                                    outline-only style sat below the help text
                                    and got skipped over. Solid blue + Sparkles
                                    icon + larger hit target make the next step
                                    visually obvious. */}
                                {selectedColorOrder.length > 0 && (
                                  // eslint-disable-next-line no-restricted-syntax -- State-dependent "Apply" CTA (blue → green-confirmed) with apply-btn-pulse animation; doesn't fit Button variants
                                  <button
                                    onClick={() => {
                                      if (selectedColorOrder.length >= 1) {
                                        const newSettings = {
                                          ...settings,
                                          primaryColor: selectedColorOrder[0],
                                          secondaryColor: selectedColorOrder[1] || settings.secondaryColor,
                                          tertiaryColor: selectedColorOrder[2] || settings.tertiaryColor
                                        };

                                        setSettings(newSettings);

                                        if (inline && onDataChange) {
                                          onDataChange(newSettings);
                                        }

                                        setAppliedField('colors');
                                        setTimeout(() => setAppliedField(null), 2000);

                                        // Show toast with color preview
                                        showToast('Brand colors applied!', 'colors', selectedColorOrder.slice(0, 3));
                                      }
                                    }}
                                    disabled={selectedColorOrder.length === 0}
                                    className={`mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                      appliedField === 'colors'
                                        ? 'bg-green-600 text-white border border-green-700'
                                        : `bg-blue-600 text-white border border-blue-700 hover:bg-blue-700 hover:shadow-md ${animateApplyButtons ? 'apply-btn-pulse' : ''}`
                                    }`}
                                  >
                                    {appliedField === 'colors' ? (
                                      <>
                                        <CheckCircle2 className="w-4 h-4" />
                                        Brand colors applied
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles className="w-4 h-4" />
                                        Apply as my brand colors ({selectedColorOrder.length}/3)
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </>
                          ) : (
                            // Extraction completed but no colors found
                            <div className="flex flex-col items-center gap-2 py-4 text-center">
                              <p className="text-sm text-ink-500">Could not extract colors from this logo</p>
                              <p className="text-xs text-ink-400">You can set brand colors manually using the color pickers below</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Business Name suggestion — hidden during onboarding (inline mode)
                            because the Business Name input is already visible in the form
                            above and a separate suggestion card just adds noise. */}
                        {!inline && (websiteData.businessName || websiteData.title) && (
                          <div className="flex items-center justify-between py-2 px-3 bg-surface rounded-lg mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Building className="h-4 w-4 text-ink-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <span className="text-sm text-ink-700 font-medium block truncate">
                                  {websiteData.businessName || websiteData.title}
                                </span>
                                {websiteData.businessName && websiteData.title && websiteData.businessName !== websiteData.title && (
                                  <span className="text-xs text-ink-400 block truncate">
                                    from: {websiteData.title}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* eslint-disable-next-line no-restricted-syntax -- State-dependent "Apply" chip (white-blue → green-confirmed); doesn't fit Button variants */}
                            <button
                              onClick={() => applyWebsiteData('name')}
                              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all flex-shrink-0 ml-2 ${
                                appliedField === 'name'
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : `bg-surface text-blue-600 border border-blue-300 hover:bg-blue-50 ${animateApplyButtons ? 'apply-btn-pulse' : ''}`
                              }`}
                            >
                              {appliedField === 'name' ? '✓ Applied' : 'Apply'}
                            </button>
                          </div>
                        )}
                        
                      </div>
                    )}
                  </div>
                </div>

                {/* Business Logo */}
                <div className={inline ? 'order-3' : ''}>
                  <label className="flex items-center gap-2 text-sm font-medium text-ink-700 mb-2">
                    <Image className="h-4 w-4" />
                    Business Logo
                  </label>
                  
                  {/* File Upload Option */}
                  <div className="mb-3">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const input = e.target;
                        const file = input.files?.[0];
                        // Always reset so re-selecting the same file fires onChange again
                        input.value = '';
                        if (!file) return;
                        await processLogoFile(file);
                      }}
                    />
                    {/* Drag-and-drop zone (also clickable to open the file picker) */}
                    {/* eslint-disable-next-line no-restricted-syntax -- Full-width dashed drop zone for logo upload; large interactive area, not a button shape */}
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Upload your logo: drag and drop an image here, or click to browse"
                      onClick={() => {
                        if (uploadingManualLogo) return;
                        setLogoUploadError(null);
                        document.getElementById('logo-upload')?.click();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (uploadingManualLogo) return;
                          setLogoUploadError(null);
                          document.getElementById('logo-upload')?.click();
                        }
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logoDragDepth.current += 1;
                        setIsDraggingLogo(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logoDragDepth.current -= 1;
                        if (logoDragDepth.current <= 0) {
                          logoDragDepth.current = 0;
                          setIsDraggingLogo(false);
                        }
                      }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        logoDragDepth.current = 0;
                        setIsDraggingLogo(false);
                        if (uploadingManualLogo) return;
                        const file = e.dataTransfer.files?.[0];
                        if (!file) return;
                        await processLogoFile(file);
                      }}
                      className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                        isDraggingLogo
                          ? 'border-green-500 bg-green-50'
                          : 'border-ink-300 bg-ink-50/50 hover:border-green-400 hover:bg-green-50/50'
                      } ${uploadingManualLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingManualLogo ? (
                        <>
                          <RefreshCw className="h-6 w-6 text-green-600 animate-spin" />
                          <span className="text-sm font-medium text-ink-700">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Download className="h-6 w-6 text-green-600" />
                          <span className="text-sm font-medium text-ink-700">
                            {isDraggingLogo ? 'Drop your logo here' : 'Drag & drop your logo here'}
                          </span>
                          <span className="text-xs text-ink-500">
                            or <span className="text-green-600 font-medium underline">click to browse</span>
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-ink-500 mt-1">
                      JPG, PNG, GIF, SVG, or WebP, max 5MB
                    </p>
                    {logoUploadError && (
                      <div
                        role="alert"
                        className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
                      >
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-red-800">{logoUploadError}</p>
                          <p className="text-11 text-red-700 mt-1">
                            Tip: Logos work best as a transparent PNG or SVG under 5MB.
                          </p>
                        </div>
                        {/* eslint-disable-next-line no-restricted-syntax -- Dismiss X on inline error banner (text-red-500 bare); too small for IconButton bare with proper hover state */}
                        <button
                          type="button"
                          onClick={() => setLogoUploadError(null)}
                          className="text-red-500 hover:text-red-700"
                          aria-label="Dismiss error"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* URL Input Option */}
                  <div className="space-y-1">
                    <label className="text-xs text-ink-600">Or enter logo URL:</label>
                    <input
                      type="url"
                      value={settings.logoPath || ''}
                      onChange={(e) => {
                        clearPendingLogoPreview();
                        updateSetting('logoPath', e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="https://example.com/logo.png"
                    />
                    <p className="text-xs text-ink-500">
                      Tip: Click "Fetch Brand Assets" above to automatically extract your logo
                    </p>
                  </div>
                  {(pendingLogoPreview || (settings.logoPath && (settings.logoPath.startsWith('http') || settings.logoPath.startsWith('blob:') || settings.logoPath.startsWith('data:')))) && (
                    <div className="mt-2 p-3 bg-ink-50 rounded-lg border border-ink-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-ink-600 font-medium">Logo Preview:</p>
                        {uploadingManualLogo && pendingLogoPreview ? (
                          <span className="flex items-center gap-1 text-xs text-ink-500">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Uploading…
                          </span>
                        ) : logoJustUploaded ? (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Uploaded
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center justify-center bg-surface p-4 rounded border border-ink-200 min-h-[100px]">
                        <img
                          src={pendingLogoPreview || settings.logoPath} 
                          alt="Business logo preview" 
                          className="max-h-20 max-w-full object-contain"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            const errorMsg = parent?.querySelector('.error-message');
                            if (!errorMsg && parent) {
                              const div = document.createElement('div');
                              div.className = 'error-message text-center';
                              div.innerHTML = `
                                <div class="text-red-600 text-sm mb-1">⚠ Failed to load logo</div>
                                <div class="text-xs text-ink-500">The image may be blocked by CORS or the URL is incorrect.</div>
                                <div class="text-xs text-ink-500 mt-1">Try: Opening the URL in a new tab to verify it works</div>
                              `;
                              parent.appendChild(div);
                            }
                          }}
                          onLoad={(e) => {
                            // Remove error message if image loads successfully
                            const errorMsg = e.currentTarget.parentElement?.querySelector('.error-message');
                            if (errorMsg) {
                              errorMsg.remove();
                            }
                          }}
                        />
                      </div>
                      {settings.logoPath && settings.logoPath.startsWith('http') && (
                        <div className="mt-2 flex items-center gap-2">
                          <a
                            href={settings.logoPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Test logo URL in new tab
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Chatbot Icon — tenant-level avatar for the booking chatbot.
                    Falls back to the company logo when empty. Hidden during
                    onboarding (inline mode); the company logo covers it there. */}
                {!inline && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
                    <MessageSquare className="h-4 w-4" />
                    Chatbot Icon
                  </label>
                  <p className="text-xs text-ink-400">
                    Shown as the chatbot's avatar in the chat header, message avatars, and the
                    launcher bubble. Falls back to your company logo when empty.
                  </p>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm ring-2 ring-white"
                      style={{
                        backgroundColor: (settings.chatbotIconUrl || settings.logoPath)
                          ? '#ffffff'
                          : (settings.primaryColor || '#6366f1'),
                      }}
                    >
                      {(settings.chatbotIconUrl || settings.logoPath) ? (
                        <img
                          src={settings.chatbotIconUrl || settings.logoPath || ''}
                          alt="Chatbot icon preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <MessageSquare className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          id="chatbot-icon-upload"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const input = e.target;
                            const file = input.files?.[0];
                            // Reset so re-selecting the same file fires onChange again.
                            input.value = '';
                            if (file) await handleChatbotIconUpload(file);
                          }}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={uploadingChatbotIcon}
                          onClick={() => {
                            if (uploadingChatbotIcon) return;
                            setChatbotIconUploadError(null);
                            document.getElementById('chatbot-icon-upload')?.click();
                          }}
                        >
                          {uploadingChatbotIcon ? (
                            <span className="flex items-center gap-1.5">
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                              Uploading…
                            </span>
                          ) : settings.chatbotIconUrl ? 'Replace icon' : 'Upload icon'}
                        </Button>
                        {settings.chatbotIconUrl && !uploadingChatbotIcon && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setChatbotIconUploadError(null);
                              updateSetting('chatbotIconUrl', '');
                            }}
                            className="text-xs text-ink-500 hover:text-ink-700 whitespace-nowrap"
                          >
                            Use company logo
                          </Button>
                        )}
                      </div>
                      <p className="text-11 text-ink-400 mt-1">
                        JPG, PNG, GIF, SVG, or WebP, max 5MB. A square image works best.
                      </p>
                    </div>
                  </div>
                  {chatbotIconUploadError && (
                    <div
                      role="alert"
                      className="bg-danger-50 border border-danger-200 rounded-lg p-3 flex items-start gap-2"
                    >
                      <AlertTriangle className="w-4 h-4 text-danger-600 flex-shrink-0 mt-0.5" />
                      <p className="flex-1 text-xs font-medium text-danger-800">{chatbotIconUploadError}</p>
                    </div>
                  )}
                </div>
                )}

                {/* Brand Colors - hidden in inline/setup guide mode (shown in sidebar instead) */}
                {!inline && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-sm font-medium text-ink-700">
                      <Palette className="h-4 w-4" />
                      Brand Colors
                    </h3>
                    {appliedField === 'colors' && (
                      <span className="text-xs text-green-600 font-medium animate-pulse">
                        ✓ Colors applied!
                      </span>
                    )}
                  </div>
                  
                  {/* Helper text */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      <span className="font-medium">💡 Booking Form Customization:</span> These colors will update the look and feel of your customer-facing booking form, giving it a personalized appearance that matches your brand.
                    </p>
                  </div>
                  
                  {/* Primary Color */}
                  <div>
                    <label className="block text-sm text-ink-600 mb-2">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.primaryColor || '#6366f1'}
                        onChange={(e) => handleColorPickerInput('primaryColor', e.target.value)}
                        className="w-12 h-10 border border-ink-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.primaryColor || '#6366f1'}
                        onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                        onBlur={(e) => handleColorBlur('primaryColor', e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          colorErrors.primaryColor ? 'border-red-500' : 'border-ink-300'
                        }`}
                        placeholder="#6366f1"
                      />
                    </div>
                    {colorErrors.primaryColor && (
                      <p className="text-xs text-red-500 mt-1">{colorErrors.primaryColor}</p>
                    )}
                    {!colorErrors.primaryColor && settings.primaryColor && isLightColor(settings.primaryColor) && (
                      <p className="text-xs text-amber-600 mt-1">
                        This color is very light — text on your booking form and chatbot will automatically switch to dark so it stays readable. Consider a deeper shade for a bolder look.
                      </p>
                    )}
                  </div>

                  {/* Secondary Color */}
                  <div>
                    <label className="block text-sm text-ink-600 mb-2">Secondary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.secondaryColor || '#64748b'}
                        onChange={(e) => handleColorPickerInput('secondaryColor', e.target.value)}
                        className="w-12 h-10 border border-ink-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.secondaryColor || '#64748b'}
                        onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                        onBlur={(e) => handleColorBlur('secondaryColor', e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          colorErrors.secondaryColor ? 'border-red-500' : 'border-ink-300'
                        }`}
                        placeholder="#64748b"
                      />
                    </div>
                    {colorErrors.secondaryColor && (
                      <p className="text-xs text-red-500 mt-1">{colorErrors.secondaryColor}</p>
                    )}
                  </div>

                  {/* Tertiary Color */}
                  <div>
                    <label className="block text-sm text-ink-600 mb-2">Tertiary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.tertiaryColor || '#f1f5f9'}
                        onChange={(e) => handleColorPickerInput('tertiaryColor', e.target.value)}
                        className="w-12 h-10 border border-ink-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.tertiaryColor || '#f1f5f9'}
                        onChange={(e) => handleColorChange('tertiaryColor', e.target.value)}
                        onBlur={(e) => handleColorBlur('tertiaryColor', e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          colorErrors.tertiaryColor ? 'border-red-500' : 'border-ink-300'
                        }`}
                        placeholder="#f1f5f9"
                      />
                    </div>
                    {colorErrors.tertiaryColor && (
                      <p className="text-xs text-red-500 mt-1">{colorErrors.tertiaryColor}</p>
                    )}
                  </div>

                  {/* Chatbot Button Color */}
                  <div>
                    <label className="block text-sm text-ink-600 mb-1">Chatbot Button Color</label>
                    <p className="text-xs text-ink-400 mb-2">The floating bubble button customers click to open your chatbot. Defaults to your primary color.</p>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.chatbotBubbleColor || settings.primaryColor || '#6366f1'}
                        onChange={(e) => handleColorPickerInput('chatbotBubbleColor', e.target.value)}
                        className="w-12 h-10 border border-ink-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.chatbotBubbleColor || ''}
                        onChange={(e) => handleColorChange('chatbotBubbleColor', e.target.value)}
                        onBlur={(e) => handleColorBlur('chatbotBubbleColor', e.target.value)}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          colorErrors.chatbotBubbleColor ? 'border-red-500' : 'border-ink-300'
                        }`}
                        placeholder={settings.primaryColor || '#6366f1'}
                      />
                      {settings.chatbotBubbleColor && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => {
                            updateSetting('chatbotBubbleColor', null);
                            setColorErrors(prev => ({ ...prev, chatbotBubbleColor: null }));
                          }}
                          className="text-xs text-ink-500 hover:text-ink-700 whitespace-nowrap"
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                    {colorErrors.chatbotBubbleColor && (
                      <p className="text-xs text-red-500 mt-1">{colorErrors.chatbotBubbleColor}</p>
                    )}
                  </div>
                </div>
                )}

                {/* Branding Preview & Summary - only in modal mode (setup guide has its own sidebar preview) */}
                {!inline && (
                  <>
                    <div className="bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-xl p-5 border border-blue-100 shadow-sm">
                      <TabbedBrandingPreview 
                        businessName={settings.businessName}
                        primaryColor={settings.primaryColor || '#6366f1'}
                        logoUrl={settings.logoPath}
                      />
                    </div>

                    <div className="bg-gradient-to-br from-ink-50 to-ink-100 rounded-lg p-4 border border-ink-200">
                      <h4 className="text-sm font-medium text-ink-900 mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Brand Summary
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-4 p-4 bg-surface rounded-lg shadow-sm border border-ink-200">
                          <CompanyLogo
                            src={settings.logoPath}
                            companyName={settings.businessName || 'Your Business'}
                            primaryColor={settings.primaryColor}
                            size="md"
                            rounded="lg"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-ink-900 truncate">
                              {settings.businessName || 'Your Business Name'}
                            </div>
                            {settings.businessWebsite && (
                              <div className="text-xs text-ink-500 truncate mt-1">
                                {settings.businessWebsite}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-surface rounded-lg p-3 border border-ink-200">
                          <p className="text-xs text-ink-600 mb-2 font-medium">Brand Colors</p>
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <div 
                                className="w-full h-12 rounded-lg border-2 shadow-sm transition-colors duration-300"
                                style={{ backgroundColor: settings.primaryColor || '#6366f1' }}
                                title={`Primary: ${settings.primaryColor || '#6366f1'}`}
                              ></div>
                              <p className="text-xs text-ink-500 mt-1 text-center font-mono">Primary</p>
                            </div>
                            <div className="flex-1">
                              <div 
                                className="w-full h-12 rounded-lg border-2 shadow-sm transition-colors duration-300"
                                style={{ backgroundColor: settings.secondaryColor || '#64748b' }}
                                title={`Secondary: ${settings.secondaryColor || '#64748b'}`}
                              ></div>
                              <p className="text-xs text-ink-500 mt-1 text-center font-mono">Secondary</p>
                            </div>
                            <div className="flex-1">
                              <div 
                                className="w-full h-12 rounded-lg border-2 shadow-sm transition-colors duration-300"
                                style={{ backgroundColor: settings.tertiaryColor || '#f1f5f9' }}
                                title={`Tertiary: ${settings.tertiaryColor || '#f1f5f9'}`}
                              ></div>
                              <p className="text-xs text-ink-500 mt-1 text-center font-mono">Tertiary</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
    </div>
  );

  // Toast content (shared between inline and modal rendering)
  const toastContent = toast ? (
    <div className={`flex items-center gap-3 ${
      toast.type === 'success'
        ? 'bg-green-50 border-green-200'
        : toast.type === 'error'
          ? 'bg-red-50 border-red-200'
          : 'bg-blue-50 border-blue-200'
    } border rounded-lg px-4 py-2.5`}>
      {toast.type === 'success' && <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />}
      {toast.type === 'colors' && <Palette className="w-5 h-5 text-blue-600 flex-shrink-0" />}
      {toast.type === 'info' && <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />}
      {toast.type === 'error' && <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${
          toast.type === 'success'
            ? 'text-green-800'
            : toast.type === 'error'
              ? 'text-red-800'
              : 'text-blue-800'
        }`}>{toast.message}</p>
        {toast.type === 'colors' && toast.colorPreview && toast.colorPreview.length > 0 && (
          <div className="flex items-center gap-2 mt-1">
            {toast.colorPreview.map((color, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-4 h-4 rounded border border-ink-300 shadow-sm" style={{ backgroundColor: color }} />
                <span className="text-2xs text-ink-600">{i === 0 ? 'Primary' : i === 1 ? 'Secondary' : 'Tertiary'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* eslint-disable-next-line no-restricted-syntax -- Toast dismiss X (bare ink-400, no padding on overlay context); too small for IconButton bare */}
      <button
        onClick={() => {
          if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
          setToast(prev => prev ? { ...prev, visible: false } : null);
          setTimeout(() => setToast(null), 300);
        }}
        className="text-ink-400 hover:text-ink-600 flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  ) : null;

  // Inline mode: sticky toast at top of the form area
  const inlineToastElement = toast ? (
    <div className={`sticky top-0 z-20 px-4 pt-3 transition-all duration-300 ${toast.visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
      {toastContent}
    </div>
  ) : null;

  // Modal mode: floating toast at top-right of the viewport
  const modalToastElement = toast ? (
    <div className="fixed top-6 right-6 z-[60] pointer-events-none" style={{ maxWidth: 380 }}>
      <div className={`pointer-events-auto ${toast.visible ? 'toast-enter' : 'toast-exit'}`}>
        {toastContent}
      </div>
    </div>
  ) : null;

  // Inline mode: Return only content (no modal wrapper, no save button)
  if (inline) {
    return <>{inlineToastElement}{contentSection}</>;
  }

  // Modal mode: Return full modal with header, content, and footer
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size="xl"
      hideHeader
      zIndex={50}
      className="!max-w-[700px]"
    >
      {/* Toast notification */}
      {modalToastElement}

      <div className="h-full flex flex-col min-h-0">
        {/* Header */}
        <div className="px-4 sm:px-6 py-2.5 sm:py-4 border-b border-ink-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center">
            <IconButton
              icon={ChevronLeft}
              shape="round"
              onClick={onClose}
              aria-label="Back"
              className="mr-2 sm:mr-3"
            />
            <div className="p-1.5 sm:p-2 bg-tone-blue-100 rounded-lg flex-shrink-0">
              <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-tone-blue-600" />
            </div>
            <div className="min-w-0 ml-2 sm:ml-3">
              <h2 className="text-lg sm:text-xl font-semibold text-ink-900">Business Branding</h2>
              <p className="text-xs sm:text-sm text-ink-500 hidden sm:block">Company name, logo, colors, and contact info</p>
            </div>
          </div>
        </div>

        {/* Content */}
        {contentSection}

        {/* Footer — matches AdTrackingPanel: right-aligned natural-size buttons */}
        <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-ink-200 flex-shrink-0">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSave} disabled={loading || uploadingLogo} loading={saving || uploadingLogo}>
            {uploadingLogo ? 'Uploading logo...' : saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EnhancedBusinessBrandingPanel;